// Plain DOM overlay, not Phaser game objects -- these panels need real HTML/CSS
// layout (text inputs, scrollable lists), so it's simplest to build them in the same
// DOM/CSS world rather than reimplementing that in Phaser's canvas API.
import { CONCEPT_LIST, ConceptId } from '../../rune-lab/src/runes/concepts';
import { OPERATOR_LIST, OPERATORS, OperatorId } from '../../rune-lab/src/runes/operators';
import { MODIFIERS } from '../../rune-lab/src/runes/modifiers';
import { CATEGORY_COLOR, CONCEPT_SHORTHAND, OPERATOR_SHORTHAND, MODIFIER_SHORTHAND } from '../../rune-lab/src/runes/shorthand';
import { resolve, magnitudeScale, CastInput, CastResult, CostTier } from '../../rune-lab/src/runes/resolve';
import { castAction } from '../../rune-lab/src/sim/cast';
import { RUNE_SYMBOL } from '../../rune-lab/src/runes/symbols';
import {
  getDisplayMode,
  setDisplayMode,
  RuneDisplayMode,
  getCastTextMode,
  setCastTextMode,
  CastTextMode,
  getSpriteId,
  setSpriteId,
  SpriteId,
  getDevMode,
  setDevMode,
  getShowRuneDescriptions,
  setShowRuneDescriptions,
  getShowEnergyDebug,
  setShowEnergyDebug,
  getShowChargeDebug,
  setShowChargeDebug,
  getShowConductivityDebug,
  setShowConductivityDebug,
  getShowHealthDebug,
  setShowHealthDebug,
  getShowEntropyDebug,
  setShowEntropyDebug,
  getShowFacingDebug,
  setShowFacingDebug,
  getShowGridCoords,
  setShowGridCoords,
  getShowDevSignposts,
  setShowDevSignposts,
  getMovementInputMode,
  setMovementInputMode,
  MovementInputMode,
  getTileSize,
  setTileSize,
  TileSize,
  getClockMode,
  setClockMode,
  ClockMode,
  getTutorialEnabled,
  setTutorialEnabled,
  getDevToolsEnabled,
  setDevToolsEnabled,
} from './settings';
import { renderRuneBoard } from './runeBoard';
import { narrateCast } from './narrate';
import { triggerCastBegan, triggerCastEnded, triggerUndo, triggerWait } from './turnActions';
import { canUndo, onUndoAvailabilityChange } from './undoHistory';
import { isTurnMode } from './clockMode';
import { getWorld } from './activeWorld';
import { getAssumption, setAssumption, getObservations, addObservation, resetNotebook, getNotebookState, setNotebookState } from './notebook';
import { isDoorLocked, setDoorLocked, resetDoors, getAllDoorLocks, setAllDoorLocks } from './doors';
import { hasItem, removeItem, getItemDef, getInventoryItems, resetInventory, getHeldItemIds, setHeldItemIds, getCollectedItemIds, setCollectedItemIds } from './inventory';
import { hasLearnedRune, resetLearnedRunes, getLearnedRuneIds, setLearnedRuneIds } from './learnedRunes';
import { triggerNewGame } from './gameReset';
import { triggerRoomRestart } from './roomRestart';
import {
  markInteractedOnce,
  getHasSeenEquipTutorial,
  markSeenEquipTutorial,
  getHasSeenCastTutorial,
  markSeenCastTutorial,
  getHasSeenModifierTutorial,
  markSeenModifierTutorial,
  getHasSeenTransferTutorial,
  markSeenTransferTutorial,
  getHasSeenNotebookTutorial,
  markSeenNotebookTutorial,
  resetTutorialHints,
} from './tutorialHints';
import { triggerRadialMeasure } from './radialMeasure';
import { getLinkedSource, getLinkedSourceName, setLinkedSource, clearLinkedSource } from './objectLink';
import { triggerTransferEffect } from './transferEffect';
import { triggerMomentumEffect } from './momentumEffect';
import { triggerProjectEffect } from './projectEffect';
import { triggerSeparateEffect } from './separateEffect';
import { triggerBindEffect } from './bindEffect';
import { triggerUnbindEffect } from './unbindEffect';
import { triggerEnergyBindEffect } from './energyBindEffect';
import { triggerUnbindEnergyEffect } from './unbindEnergyEffect';
import { triggerChargeBindEffect } from './chargeBindEffect';
import { triggerEntropyBindEffect } from './entropyBindEffect';
import { triggerUnbindEntropyEffect } from './unbindEntropyEffect';
import { triggerUnbindChargeEffect } from './unbindChargeEffect';
import { triggerDpadDirection, triggerDpadRelease, DpadDirection } from './dpadInput';
import { triggerRoomJump } from './roomJump';
import { triggerInteractEffects } from './interactEffects';
import { applyInteract } from '../../rune-lab/src/sim/interact';
import { downloadTake, recordCommand, takeCommandCount, takeRoomId } from './moveRecorder';
import { SimEvents } from '../../rune-lab/src/sim/events';
import { REVISION_HISTORY } from './revisionHistory';
import { trackEvent, getAnalyticsPlayerId } from './analytics';
import { reportError, showErrorNotice } from './errorTelemetry';
import { SaveData, hasSave, readSave, writeSave, getRoomSnapshot, applyRoomSnapshot } from './gameSave';
import { getMana, getHealth, getMaxMana, getMaxHealth, getThermalState, getMaxThermalState, getThermalFrozenThreshold, getThermalBurningThreshold, resetPlayerStats, getPlayerStatsSnapshot, setPlayerStatsSnapshot } from './playerStats';
import {
  ActionId,
  ACTION_IDS,
  ACTION_LABELS,
  getKeybinding,
  setKeybinding,
  clearKeybinding,
  findActionForCode,
  getHideMappedButtons,
  setHideMappedButtons,
  onKeybindingsChange,
  formatKeyCode,
} from './keybindings';

// Same engine rune-lab's Play/Simulator tabs use -- resolve() is stateless (no
// persistent object state, no real physics engine), so "casting" here just means
// computing that same dimensional-analysis/cost/effect result and reporting it as
// text, exactly like those tabs do, rather than simulating what happens to the
// target object over time.
const TIER_COLOR: Record<CostTier, string> = {
  trivial: '#4caf50',
  modest: '#c9a24b',
  significant: '#d9793f',
  catastrophic: '#d9473f',
};

type RuneEntry = { id: string; name: string };

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

const ALL_RUNES: RuneEntry[] = [
  ...CONCEPT_LIST.map((c) => ({ id: c.id, name: c.name })),
  ...OPERATOR_LIST.map((o) => ({ id: o.id, name: o.name })),
  ...MODIFIERS.map((m) => ({ id: m.id, name: m.name })),
];

function runeColor(id: string): string {
  if (CONCEPT_LIST.some((c) => c.id === id)) return CATEGORY_COLOR.concept;
  if (OPERATOR_LIST.some((o) => o.id === id)) return CATEGORY_COLOR.operator;
  return CATEGORY_COLOR.modifier;
}

function runeShorthand(id: string): string {
  return (
    (CONCEPT_SHORTHAND as Record<string, string>)[id] ??
    (OPERATOR_SHORTHAND as Record<string, string>)[id] ??
    MODIFIER_SHORTHAND[id] ??
    id
  );
}

let openPanelId: string | null = null;
// State for the Menu button's secret Developer Tools unlock -- see initUI().
let menuSecretTapCount = 0;
let menuSecretTapTimer: ReturnType<typeof setTimeout> | null = null;
let selectedRuneId: string | null = null;
// Set the instant both Energy and Project are learned (see
// resolveInteractText); consumed the next time the interact-panel closes
// (see closePanel), since opening the first tutorial panel any earlier would
// just get closed right back out by that still-open panel.
let pendingEquipTutorial = false;
// A few ticks, not instant -- the second tutorial panel (explaining Cast and
// the candle) fires this long after the equip panel closes with both runes
// sitting in the same hotbar slot, so it reads as a follow-up nudge once the
// player's had a moment to look at what they just built, not a popup that
// slams shut one panel and immediately opens another in its place.
const CAST_TUTORIAL_DELAY_MS = 1500;
// Same "consumed the next time the interact-panel closes" shape as
// pendingEquipTutorial above, for the standalone rune-learned popups below
// (Modifier, Transfer) -- each fires off its own bookshelf's own interact,
// not off some other panel closing.
let pendingModifierTutorial = false;
let pendingTransferTutorial = false;
// Same shape again, but off room2's chest (see resolveInteractText's
// giveItemId branch) rather than a bookshelf's giveRuneId -- the first pause
// point after the player has two real runes in hand.
let pendingNotebookTutorial = false;
// "A couple of seconds," not instant, matching CAST_TUTORIAL_DELAY_MS's own
// reasoning -- these popups follow the interact-panel closing rather than
// opening in its place.
const TUTORIAL_POPUP_DELAY_MS = 2000;

export function isMenuOpen(): boolean {
  return openPanelId !== null;
}

// The title screen and the intro that follows it both gate the very start of
// a life -- nothing the player does there is gameplay yet, so GameScene uses
// this to hold off its ambient Energy regen/decay timers until a life
// actually begins, rather than letting the world's clock run in the
// background before the player has even woken up. Distinct from the general
// isMenuOpen() (in-game menus deliberately don't pause ambient decay).
export function isPreGameScreenOpen(): boolean {
  return openPanelId === 'title-panel' || openPanelId === 'intro-panel';
}

// --- Dialogue-box keyboard navigation ---------------------------------------------
// Whatever panel is currently open (the interact panel, a locked door's prompt,
// the main Settings/Runes/Inventory panel, ...) gets one "focused" button that
// Up/Down cycles through and Interact activates -- see the keydown wiring in
// initUI(). This is deliberately generic over *any* open `.ui-panel` rather than
// a bespoke "dialogue box" type, since every panel already renders its buttons
// as plain <button> elements.
let focusedPanelButtonIndex = 0;

function getOpenPanelButtons(): HTMLButtonElement[] {
  if (!openPanelId) return [];
  const panel = document.getElementById(openPanelId);
  if (!panel) return [];
  return Array.from(panel.querySelectorAll<HTMLButtonElement>('button')).filter(
    (btn) => !btn.disabled && btn.offsetParent !== null,
  );
}

function highlightFocusedPanelButton(): void {
  const buttons = getOpenPanelButtons();
  buttons.forEach((btn, i) => btn.classList.toggle('keynav-focused', i === focusedPanelButtonIndex));
}

function rectCenter(el: HTMLElement): { x: number; y: number } {
  const r = el.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}

// Spatial "nearest neighbor in this direction" search over a panel's buttons,
// using their actual on-screen position -- what makes Up/Down/Left/Right
// navigate like a real matrix (the Equip panel's rune board wraps into
// several rows of varying width) rather than only ever stepping through one
// flat, single-column array. Perpendicular misalignment is penalized more
// heavily than distance along the movement axis, the standard heuristic for
// this kind of directional focus search, so a button roughly above/below (or
// left/right) of the current one wins over one that's merely closer as the
// crow flies but off to the side.
function nearestButtonInDirection(
  buttons: HTMLButtonElement[],
  from: HTMLButtonElement,
  dir: 'up' | 'down' | 'left' | 'right',
): HTMLButtonElement | null {
  const origin = rectCenter(from);
  let best: HTMLButtonElement | null = null;
  let bestScore = Infinity;
  for (const btn of buttons) {
    if (btn === from) continue;
    const c = rectCenter(btn);
    const dx = c.x - origin.x;
    const dy = c.y - origin.y;
    const primary = dir === 'up' ? -dy : dir === 'down' ? dy : dir === 'left' ? -dx : dx;
    if (primary <= 1) continue; // must be a genuine step in that direction
    const perpendicular = dir === 'up' || dir === 'down' ? Math.abs(dx) : Math.abs(dy);
    const score = primary + perpendicular * 3;
    if (score < bestScore) {
      bestScore = score;
      best = btn;
    }
  }
  return best;
}

function movePanelFocusDirectional(dir: 'up' | 'down' | 'left' | 'right'): void {
  const buttons = getOpenPanelButtons();
  if (!buttons.length) return;
  const current = buttons[Math.min(focusedPanelButtonIndex, buttons.length - 1)];
  let target = current ? nearestButtonInDirection(buttons, current, dir) : null;
  // Up/Down wrap at the ends of the whole button list, preserving the old
  // flat-cycle behavior for panels that are just one column deep (Settings,
  // Interact, door prompts, ...); Left/Right deliberately don't wrap -- a
  // grid's rows can differ in length, and "wrap to the far side" has no
  // single obvious meaning there the way top<->bottom does for one column.
  if (!target && current && (dir === 'up' || dir === 'down')) {
    target = dir === 'down' ? buttons[0] : buttons[buttons.length - 1];
  }
  if (!target) return;
  focusedPanelButtonIndex = buttons.indexOf(target);
  highlightFocusedPanelButton();
}

// Returns whether a button actually got activated, so the Interact keydown
// handler knows whether to fall through to its normal "open the interact
// panel" behavior instead.
function activateFocusedPanelButton(): boolean {
  const buttons = getOpenPanelButtons();
  if (!buttons.length) return false;
  buttons[Math.min(focusedPanelButtonIndex, buttons.length - 1)]?.click();
  return true;
}

function openPanel(id: string): void {
  // Only one panel at a time -- opening one closes any other that's already up,
  // rather than letting them stack.
  document.querySelectorAll<HTMLElement>('.ui-panel').forEach((panel) => {
    if (panel.id !== id) panel.classList.add('hidden');
  });
  document.getElementById(id)?.classList.remove('hidden');
  openPanelId = id;
  focusedPanelButtonIndex = 0;
  // Callers render a panel's content either before or after this call --
  // deferring one tick guarantees its buttons already exist in the DOM by the
  // time this runs either way, instead of highlighting nothing.
  setTimeout(() => {
    if (openPanelId === id) highlightFocusedPanelButton();
  }, 0);
}

function closePanel(id: string): void {
  document.getElementById(id)?.classList.add('hidden');
  if (openPanelId === id) openPanelId = null;
  if (id === 'main-panel') selectedRuneId = null;
  // Deferred rather than shown immediately when the second rune is learned
  // (see resolveInteractText): opening a second panel on top of the
  // still-open interact-panel would just close it right back out (openPanel
  // only ever allows one at a time), so the earliest safe moment is the
  // instant that panel itself closes, however the player closed it (the
  // close button, Escape, or the keybound Interact action all funnel
  // through here).
  if (id === 'interact-panel' && pendingEquipTutorial) {
    pendingEquipTutorial = false;
    showEquipTutorial();
  }
  // Unlike the Equip/Cast pair above, these two fire off the interact-panel
  // closing directly (each is its own standalone popup, not the second half
  // of a combo) -- but still deferred a couple of seconds via setTimeout
  // rather than opening the instant this panel closes, so it reads as a
  // follow-up nudge rather than one panel slamming into the next.
  if (id === 'interact-panel' && pendingModifierTutorial) {
    pendingModifierTutorial = false;
    setTimeout(() => {
      if (!getHasSeenModifierTutorial()) showModifierTutorial();
    }, TUTORIAL_POPUP_DELAY_MS);
  }
  if (id === 'interact-panel' && pendingTransferTutorial) {
    pendingTransferTutorial = false;
    setTimeout(() => {
      if (!getHasSeenTransferTutorial()) showTransferTutorial();
    }, TUTORIAL_POPUP_DELAY_MS);
  }
  if (id === 'interact-panel' && pendingNotebookTutorial) {
    pendingNotebookTutorial = false;
    setTimeout(() => {
      if (!getHasSeenNotebookTutorial()) showNotebookTutorial();
    }, TUTORIAL_POPUP_DELAY_MS);
  }
  // The second panel fires off the *equip* panel closing, not the interact
  // panel -- only meaningful once the first panel has already been seen (so
  // this doesn't fire on some unrelated early equip), only once ever (see
  // getHasSeenCastTutorial), and only if the player actually built the real
  // combo (Energy + Project together in one hotbar slot) rather than just
  // opening and closing the panel without equipping anything.
  if (
    id === 'equip-panel' &&
    getTutorialEnabled() &&
    getHasSeenEquipTutorial() &&
    !getHasSeenCastTutorial() &&
    equippedSlots.some((slot) => slot.conceptId === 'energy' && slot.operatorId === 'project')
  ) {
    setTimeout(() => {
      if (!getHasSeenCastTutorial()) showCastTutorial();
    }, CAST_TUTORIAL_DELAY_MS);
  }
}

// --- Main panel: Settings / Runes / Inventory tabs ------------------------------
// Menu, Notebook, and Inventory used to be three separate buttons opening three
// separate panels; condensed into one panel behind one button, with a tab bar
// (same `.notebook-tab-btn` shape the Runes tab already used when it was the
// notebook's only tab) selecting which section's content renders into the shared
// `#main-panel-content` div.

type MainTab = 'settings' | 'runes' | 'inventory';
let activeMainTab: MainTab = 'settings';

function renderMainPanelContent(): void {
  document.querySelectorAll<HTMLButtonElement>('#main-panel .notebook-tab-btn').forEach((btn) => {
    btn.classList.toggle('active', btn.dataset.tab === activeMainTab);
  });
  if (activeMainTab === 'runes') {
    renderRunesTab();
  } else if (activeMainTab === 'inventory') {
    renderInventoryTab();
  } else {
    renderSettingsTab();
  }
}

function renderSettingsTab(): void {
  const content = document.getElementById('main-panel-content');
  if (!content) return;

  content.innerHTML = `
    <div class="settings-group">
      <span class="settings-label">Save / Resume / New Game</span>
      <div id="save-load-controls"></div>
    </div>
    <div class="settings-group">
      <span class="settings-label">Sprite</span>
      <div class="settings-options">
        <label class="settings-option"><input type="radio" name="sprite-id" value="default" /> Default</label>
        <label class="settings-option"><input type="radio" name="sprite-id" value="witch" /> Witch</label>
        <label class="settings-option"><input type="radio" name="sprite-id" value="wizard" /> Wizard</label>
      </div>
    </div>
    <div class="settings-group">
      <span class="settings-label">Movement controls</span>
      <div class="settings-options">
        <label class="settings-option"><input type="radio" name="movement-input-mode" value="quadrant" /> Tap screen quadrants</label>
        <label class="settings-option"><input type="radio" name="movement-input-mode" value="dpad" /> On-screen D-pad</label>
      </div>
    </div>
    <div class="settings-group">
      <span class="settings-label">Tile size</span>
      <div class="settings-options">
        <label class="settings-option"><input type="radio" name="tile-size" value="24" /> Small (24px)</label>
        <label class="settings-option"><input type="radio" name="tile-size" value="32" /> Default (32px)</label>
        <label class="settings-option"><input type="radio" name="tile-size" value="48" /> Large (48px)</label>
        <label class="settings-option"><input type="radio" name="tile-size" value="64" /> Huge (64px)</label>
      </div>
      <p class="muted small">
        How close the view sits. Larger tiles are easier to read and to tap, and show
        less of the room at once; smaller ones take a big room in at a glance.
      </p>
    </div>
    <div class="settings-group">
      <span class="settings-label">Time</span>
      <div class="settings-options">
        <label class="settings-option"><input type="radio" name="clock-mode" value="wall" /> Real-time</label>
        <label class="settings-option"><input type="radio" name="clock-mode" value="turn" /> Turn-based (with undo)</label>
      </div>
      <p class="muted small">
        In real-time the world keeps decaying and recovering while you think. Turn-based
        advances it by one step for each thing you do — and lets you take a step back.
      </p>
    </div>
    <div class="settings-group">
      <span class="settings-label">Keyboard shortcuts</span>
      <div id="keybindings-controls"></div>
    </div>
    ${getDevToolsEnabled() ? '<button id="dev-tools-button" class="ui-button">Developer Tools</button>' : ''}`;

  content.querySelectorAll<HTMLInputElement>('input[name="sprite-id"]').forEach((radio) => {
    radio.checked = radio.value === getSpriteId();
    radio.addEventListener('change', () => {
      if (radio.checked) setSpriteId(radio.value as SpriteId);
    });
  });
  content.querySelectorAll<HTMLInputElement>('input[name="movement-input-mode"]').forEach((radio) => {
    radio.checked = radio.value === getMovementInputMode();
    radio.addEventListener('change', () => {
      if (radio.checked) {
        setMovementInputMode(radio.value as MovementInputMode);
        applyMovementInputMode();
      }
    });
  });
  content.querySelectorAll<HTMLInputElement>('input[name="tile-size"]').forEach((radio) => {
    radio.checked = Number(radio.value) === getTileSize();
    radio.addEventListener('change', () => {
      // No apply step here, same as the clock below: the scene subscribes to
      // onTileSizeChange and re-zooms its own camera, which is not this
      // module's to touch.
      if (radio.checked) setTileSize(Number(radio.value) as TileSize);
    });
  });
  content.querySelectorAll<HTMLInputElement>('input[name="clock-mode"]').forEach((radio) => {
    radio.checked = radio.value === getClockMode();
    radio.addEventListener('change', () => {
      // No applyClockMode() call to match applyMovementInputMode() above: the
      // scene subscribes to onClockModeChange and does the applying, because what
      // has to change is the world's own physics and the undo history, neither of
      // which this module owns. refreshTurnHud is the part that *is* the DOM's
      // job, and it rides on the same notification.
      if (radio.checked) setClockMode(radio.value as ClockMode);
    });
  });
  content.querySelector('#dev-tools-button')?.addEventListener('click', () => {
    openPanel('dev-tools-panel');
    renderRoomJumpButtons();
  });
  renderSaveLoadControls();
  renderKeybindingsControls();
}

// --- Keyboard shortcuts ------------------------------------------------------------
// PC-only convenience: bind a real keyboard key to any of the always-visible HUD
// action buttons (and each of the 4 quadrant hotbar slots), as an alternative to
// clicking/tapping them. Movement (WASD/arrows) is unaffected either way -- this is
// purely about the buttons. See the keydown/keyup wiring in initUI() for how a bound
// key actually fires its action.

// Set while a "Set" button is waiting for the player's next keypress -- the same
// window keydown listener that dispatches bound actions checks this first and
// captures the key instead, so only one listener is ever needed for both jobs.
let listeningForAction: ActionId | null = null;

function renderKeybindingsControls(): void {
  const container = document.getElementById('keybindings-controls');
  if (!container) return;

  const rowsHtml = ACTION_IDS.map((action) => {
    const code = getKeybinding(action);
    const isListening = listeningForAction === action;
    const keyLabel = isListening ? 'Press a key…' : code ? formatKeyCode(code) : 'Unbound';
    return `
      <div class="keybind-row">
        <span class="keybind-label">${ACTION_LABELS[action]}</span>
        <span class="keybind-key">${keyLabel}</span>
        <button class="ui-button keybind-set-button" data-action="${action}">${isListening ? 'Cancel' : 'Set'}</button>
        <button class="ui-button keybind-clear-button" data-action="${action}" ${code ? '' : 'disabled'}>Clear</button>
      </div>`;
  }).join('');

  container.innerHTML = `
    <div class="keybind-rows">${rowsHtml}</div>
    <label class="settings-option">
      <input type="checkbox" id="hide-mapped-buttons-toggle" />
      Hide HUD buttons once mapped to a key (Menu always stays visible)
    </label>`;

  container.querySelectorAll<HTMLButtonElement>('.keybind-set-button').forEach((btn) => {
    btn.addEventListener('click', () => {
      const action = btn.dataset.action as ActionId;
      listeningForAction = listeningForAction === action ? null : action;
      renderKeybindingsControls();
    });
  });
  container.querySelectorAll<HTMLButtonElement>('.keybind-clear-button').forEach((btn) => {
    btn.addEventListener('click', () => {
      clearKeybinding(btn.dataset.action as ActionId);
      renderKeybindingsControls();
    });
  });

  const hideToggle = container.querySelector('#hide-mapped-buttons-toggle') as HTMLInputElement | null;
  if (hideToggle) {
    hideToggle.checked = getHideMappedButtons();
    hideToggle.addEventListener('change', () => setHideMappedButtons(hideToggle.checked));
  }
}

function renderRunesTab(): void {
  const content = document.getElementById('main-panel-content');
  if (!content) return;

  if (selectedRuneId) {
    content.innerHTML = renderRuneDetail(selectedRuneId);
    content.querySelector('.notebook-back')?.addEventListener('click', () => {
      selectedRuneId = null;
      renderRunesTab();
    });
    const textarea = content.querySelector<HTMLTextAreaElement>('.assumption-input');
    textarea?.addEventListener('input', () => {
      if (selectedRuneId) setAssumption(selectedRuneId, textarea.value);
      // The name just typed shows up live in the Equip panel and the hotbar too
      // (see runeNameTag), both of which are plain DOM state outside this tab.
      refreshQuadrantUI();
    });
    return;
  }

  content.innerHTML = renderRuneBoard();
  content.querySelectorAll<HTMLButtonElement>('.rune-board-chip').forEach((btn) => {
    btn.addEventListener('click', () => {
      selectedRuneId = btn.dataset.runeId ?? null;
      renderRunesTab();
    });
  });
}

function renderRuneDetail(runeId: string): string {
  const rune = ALL_RUNES.find((r) => r.id === runeId);
  const color = runeColor(runeId);
  const symbolMode = getDisplayMode() === 'symbols';
  const label = symbolMode ? (RUNE_SYMBOL[runeId] ?? '?') : runeShorthand(runeId);
  const nameHtml = symbolMode ? '' : ` <span class="rune-detail-name">${rune?.name ?? ''}</span>`;
  const assumption = getAssumption(runeId);
  const observations = getObservations(runeId);
  const observationsHtml = observations.length
    ? `<ul class="observation-list">${observations.map((o) => `<li>${o}</li>`).join('')}</ul>`
    : '<p class="muted small">No observations yet -- cast this rune on something to start finding out.</p>';

  return `
    <button class="notebook-back">&larr; Back</button>
    <div class="rune-detail-header" style="color:${color}; border-color:${color}">${label}${nameHtml}</div>
    <div class="rune-detail-section">
      <span class="settings-label">Name this rune</span>
      <textarea class="assumption-input" placeholder="Give this rune a name of your own" rows="3">${escapeHtml(assumption)}</textarea>
    </div>
    <div class="rune-detail-section">
      <span class="settings-label">Observations from spells cast</span>
      ${observationsHtml}
    </div>`;
}

// --- Equipped rune line + facing-based targeting --------------------------------
// Replaces the old per-object casting window: instead of tapping a specific
// object's sprite to open a fresh rune board scoped to it, the player builds one
// persistent "equipped" line (concept -> operator -> second concept if needed,
// plus modifiers) in the Equip panel below, then fires it at any time with the
// always-available Cast button. This is a loadout of individual runes, not a
// saved preset combo -- equipping never skips composing the expression at cast
// time, which is the line CLAUDE.md draws against turning into a predefined
// spell list. The target is resolved fresh on every cast from whatever's
// directly ahead of the player (see GameScene's pushFacedTarget), falling back
// to the caster when nothing's there -- the same "no target = self" default
// Transfer's marked-source logic already used, now covering the target locus
// too.
//
// Mirrors rune-lab's Play tab for the armed-slot wizard itself (concept ->
// operator -> second concept if the operator needs one), with modifiers
// toggled independently of the arm progression, and a slot preview row whose
// chips are themselves clickable to jump the armed pointer back for edits --
// same pattern as before, just against a global line instead of a per-object one.

type CastSlot = 'concept' | 'operator' | 'secondConcept';

interface CastLine {
  conceptId: string | null;
  operatorId: string | null;
  secondConceptId: string | null;
  modifierIds: Set<string>;
}

function emptyCastLine(): CastLine {
  return { conceptId: null, operatorId: null, secondConceptId: null, modifierIds: new Set() };
}

interface ObjectInteract {
  label: string;
  /** The immersive description -- never rewritten across repeat visits. If
   * giveItemId/giveRuneId is set, resolveInteractText appends a second,
   * purely mechanical line reporting what was granted (or already was)
   * rather than baking that into this text itself. */
  text: string;
  /** Item id granted the first time this interaction happens. */
  giveItemId?: string;
  /** Rune id learned the first time this interaction happens (books). */
  giveRuneId?: string;
  /** Reserved for a future "already read this" state on plain flavor-only
   * interacts (no giveItemId/giveRuneId) -- not consumed by resolveInteractText
   * today, which always shows `text` for those. */
  repeatText?: string;
  /** Item id this interaction consumes from inventory on success -- the
   * reverse of giveItemId. Without the item, nothing happens beyond showing
   * `text`; with it, resolveInteractText removes it from inventory and
   * the altar is filled in `sim/interact.ts` and the resulting events are
   * routed to the owning GameScene (room10's pillars). */
  placeItemId?: string;
  /** Lock id (matching a DoorDef's own lockId) this interaction unlocks the
   * first time it happens -- a lever/switch, distinct from giveItemId's
   * carry-a-key model: nothing changes hands, the door itself just
   * permanently unlocks in place. */
  unlocksDoorId?: string;
  /** If true, this interaction's panel appends a short feedback form below
   * the flavor text (rating + free text, POSTed to /api/feedback). Only the
   * Portal (the demo's endpoint) sets this today, but it's a generic flag. */
  showFeedbackForm?: boolean;
}

/** Whatever's directly ahead of the player right now, pushed by GameScene
 * whenever position/facing (or the faced object itself) changes -- see
 * updateFacedTarget below. `linkable` is true for anything worth marking as a
 * Source: an object with mutable `properties` (Transfer reads/writes it) or
 * a `pushable` one (a realistic Bind·Momentum locus, now that marking feeds
 * that too, not just Transfer). */
export interface FacedTarget {
  id: string;
  name: string;
  interact?: ObjectInteract;
  linkable: boolean;
}

let facedTarget: FacedTarget | null = null;
// Four independently-equippable rune expressions ("quadrants") instead of one
// global line -- a Pokemon-style hotbar of pre-built loadouts, switched
// between instantly (tapping a quadrant), rather than rebuilt from scratch
// every cast. Still never a saved *combo*: each quadrant is itself a loadout
// of individual runes assembled the same armed-slot way as before, so this
// doesn't cross into the predefined-spell territory CLAUDE.md rules out --
// it just lets four such loadouts sit ready at once instead of one.
const QUADRANT_COUNT = 4;
let equippedSlots: CastLine[] = Array.from({ length: QUADRANT_COUNT }, () => emptyCastLine());
let activeSlotIndex = 0;
let equipArmedSlot: CastSlot = 'concept';
let resolvedFlavorText = '';
let measureHoldInterval: ReturnType<typeof setInterval> | null = null;

function currentEquippedLine(): CastLine {
  return equippedSlots[activeSlotIndex];
}

// GameScene has no reference to this module's DOM state and calls this
// directly (same one-directional shape as updateStatsHud) every time the
// faced tile's content might have changed: after a move attempt, after
// entering a room, and after anything that removes an object.
export function updateFacedTarget(target: FacedTarget | null): void {
  facedTarget = target;
  renderActionButtons();
}

// Prefixes a mechanical confirmation line (see renderInteractPanel) so it
// renders bold/orange -- covers the one-time "you just got this" grants and
// the repeat "you already know this rune" line (worth calling out the same
// way), but not the repeat "you already have this item" line, which stays
// plain.
const GRANT_LINE_MARKER = '';

// Resolves the interaction's flavor text, granting its item/rune on first use. A
// generic hook (any interact can carry a giveItemId or giveRuneId), not something
// special-cased to "the crate" or "the bookshelf" -- what's granted, if anything,
// comes from the object's own data. Give-granting interacts always return two
// lines (joined by "\n", split back apart in renderInteractPanel): `interact.text`
// itself, the immersive description, which never changes across repeat visits;
// and a second, mechanical line reporting what just happened (or already
// happened) -- kept separate so the description doesn't have to be rewritten into
// a whole new paragraph just because the state changed. Plain flavor-only
// interacts (no giveItemId/giveRuneId) are unaffected -- just `interact.text`.
function resolveInteractText(objectId: string, interact: ObjectInteract | undefined): string {
  if (!interact) return '';

  // The mutation half moved to `sim/interact.ts` (2026-08-13). This function
  // used to grant the item *and* compose the sentence about having granted it,
  // interleaved with analytics and tutorial triggers -- the same interleaving
  // `sim/events.ts` was created to end, arrived at from the other direction.
  //
  // What is left here is exactly what belongs here: the voice. Every branch
  // below reads one tagged fact off the outcome -- `already` or `missing` --
  // and says something about it. Nothing below writes to the world.
  const events: SimEvents = [];
  recordCommand({ kind: 'interact', id: objectId });
  const outcome = applyInteract(getWorld(), objectId, events);
  // The events have to reach the renderer or the fourth crystal fills an altar
  // nobody can see fill, and room10's Portal stays invisible until the player
  // leaves and comes back: `applyInteract` settles, and settling is where
  // `checkRuneAltars` performs the swap.
  triggerInteractEffects(events);

  // Deliberately *not* charging a turn or taking an undo point: the interact
  // button has never been either. The sim's `interact` command used to charge
  // one, which meant a recording and a search treated opening a crate as a move
  // while the game did not; that divergence was left standing here on purpose
  // (settling a gameplay question inside a refactor is the
  // behaviour-decision-in-disguise `14_Refactoring_Method.md` says to refuse)
  // and was answered on 2026-08-14 the other way round -- the sim went free, to
  // match this. So there is nothing left to reconcile, and the reason this line
  // does not call `advanceTurn` is now simply that rummaging in a box is not a
  // move. `applyCommand`'s header holds the rule.

  switch (outcome.kind) {
    case 'item': {
      const itemName = getItemDef(outcome.itemId)?.name ?? 'the item';
      if (!outcome.already) {
        trackEvent('item_collected', { itemId: outcome.itemId, roomId: getCurrentRoomId() });
        // room2's chest specifically: the Warded Key only exists to hand out
        // once the Guttering Candle has already taken both Energy and Project
        // to light and melt, so by now the player always holds two real runes
        // -- the first natural pause to point at the Runes tab's naming field.
        // Deferred until the interact-panel this text is shown in actually
        // closes (see closePanel), same as every other standalone nudge.
        if (getTutorialEnabled() && outcome.itemId === 'warded-key' && !getHasSeenNotebookTutorial()) {
          pendingNotebookTutorial = true;
        }
        return `${interact.text}\n${GRANT_LINE_MARKER}You add the ${itemName} to your inventory.`;
      }
      // `already` covers both "still carrying it" and "took it and spent it" --
      // the distinction `collectedItemIds` exists to make, and the two read
      // differently to a player standing in front of an empty crate.
      if (hasItem(outcome.itemId)) return `${interact.text}\nYou already have the ${itemName}.`;
      return `${interact.text}\nThere's nothing left here -- you already took the ${itemName}.`;
    }

    case 'rune': {
      // The glyph, never the English name -- "nothing here should hint at what a
      // rune does" (see runeSymbols.ts) is a hard rule the rest of the game's
      // flavor text already follows; this mechanical confirmation line doesn't
      // get an exception just because it's new.
      const symbol = RUNE_SYMBOL[outcome.runeId] ?? outcome.runeId;
      if (outcome.already) return `${interact.text}\n${GRANT_LINE_MARKER}You already know the "${symbol}" rune.`;
      trackEvent('rune_learned', { runeId: outcome.runeId, roomId: getCurrentRoomId() });
      // The first two runes that pair into a real, dramatic cast (Project's
      // sealed Guttering Candle in room2) -- the instant both are held,
      // regardless of which one this happened to be, queue the first of two
      // tutorial panels (teaching Equip; the second, teaching Cast, follows
      // once the player actually builds the combo -- see closePanel).
      // Deferred until the interact-panel this text is shown in actually
      // closes (see closePanel) rather than shown here.
      if (getTutorialEnabled() && hasLearnedRune('energy') && hasLearnedRune('project') && !getHasSeenEquipTutorial()) {
        pendingEquipTutorial = true;
      }
      // First Modifier ever learned (no other modifier already known) --
      // Concept/Operator alone don't hint that a third rune category even
      // exists, so this is a standalone nudge rather than something the
      // player would otherwise stumble onto in the Equip panel.
      if (
        getTutorialEnabled() &&
        MODIFIERS.some((m) => m.id === outcome.runeId) &&
        !MODIFIERS.some((m) => m.id !== outcome.runeId && hasLearnedRune(m.id)) &&
        !getHasSeenModifierTutorial()
      ) {
        pendingModifierTutorial = true;
      }
      // Transfer specifically -- Mark as Source has no other tutorial nudge
      // anywhere (room4's own bookshelf-5 explains it narratively, not
      // mechanically), and it's the one rune whose controls aren't
      // discoverable purely from the Equip/Cast tutorial above.
      if (getTutorialEnabled() && outcome.runeId === 'transfer' && !getHasSeenTransferTutorial()) {
        pendingTransferTutorial = true;
      }
      return `${interact.text}\n${GRANT_LINE_MARKER}Something about the rune ${symbol} clicks.`;
    }

    case 'placed': {
      if (outcome.missing) return `${interact.text}\nNothing you're carrying fits this slot.`;
      const itemName = getItemDef(outcome.itemId)?.name ?? 'the item';
      return `${interact.text}\n${GRANT_LINE_MARKER}The ${itemName} settles into the slot, and something shifts.`;
    }

    case 'breaker': {
      // The description is re-read off the object rather than taken from the
      // `interact` captured above, and it is the only branch that has to be:
      // throwing a breaker rewrites its own name *and* its own description
      // (`sim/breaker.ts` owns both), so the captured one describes the position
      // it was in a moment ago. Everything else here grants something and leaves
      // the object saying exactly what it always said -- which is why the
      // capture is right for them and wrong for this. Printing the stale line
      // under the new title is the "the description changed and the title did
      // not" drift `presentation.ts` exists to prevent, arriving from the far
      // side of the sim boundary.
      const thrown = getWorld().rooms[getCurrentRoomId()]?.objects.find((o) => o.id === objectId);
      const description = thrown?.interact?.text ?? interact.text;
      // Deliberately says nothing about what happened *downstream*. Which lamp
      // went out, which gate shut, is the thing the room is supposed to teach by
      // being looked at -- and the sim has already settled by the time this
      // runs, so it is all on screen.
      return outcome.open
        ? `${description}\n${GRANT_LINE_MARKER}The blade comes up out of its housing with a bang, and the line goes dead.`
        : `${description}\n${GRANT_LINE_MARKER}The blade drops home, and whatever is on the far side of it wakes up.`;
    }

    case 'conveyor': {
      // Re-read off the object for the same reason the breaker branch does, and
      // it is the same mechanism doing it: a belt's description names which way
      // it runs (`rust.ts`'s CONVEYOR table), so reversing rewrites every
      // segment -- but the console's own text is untouched, which is why this
      // one reads its own captured line and reports the run instead.
      if (outcome.belts === 0) return `${interact.text}\nThe lever throws with a clunk. Nothing is standing against this to hear it.`;
      // Deliberately says nothing about *which* way it now runs. The arrows on
      // the belt say that, and they said it before the lever was thrown -- a
      // machine whose state you have to read a message box to know is a machine
      // that has stopped being worth looking at.
      const run = outcome.belts === 1 ? 'the belt beside it' : `all ${outcome.belts} lengths of belt`;
      return `${interact.text}\n${GRANT_LINE_MARKER}The lever goes over, and ${run} rolls the other way.`;
    }

    case 'unlocked':
      if (outcome.already) return `${interact.text}\nIt's already thrown -- the door stays open.`;
      return `${interact.text}\n${GRANT_LINE_MARKER}Somewhere nearby, a door unlocks with a heavy thud.`;

    case 'none':
      return interact.text;
  }
}

function operatorRequiresSecondConcept(operatorId: string): boolean {
  return (OPERATORS as Record<string, { requiresSecondConcept: boolean }>)[operatorId].requiresSecondConcept;
}

function needsSecondConcept(): boolean {
  const line = currentEquippedLine();
  return !!line.operatorId && operatorRequiresSecondConcept(line.operatorId);
}

function runeLabel(id: string): string {
  return getDisplayMode() === 'symbols' ? RUNE_SYMBOL[id] ?? '?' : runeShorthand(id);
}

// The player's own nickname for a rune (typed in the Rune notebook, see
// renderRuneDetail) -- reused wherever that rune's chip is drawn (the Equip
// panel's live preview, the hotbar) so naming it once surfaces everywhere.
// Empty until the player bothers to type one, so this is invisible by default.
function runeNameTag(id: string): string {
  const name = getAssumption(id).trim();
  return name ? `<span class="rune-name-tag">${escapeHtml(name)}</span>` : '';
}

function slotChipHtml(slot: CastSlot, id: string | null, placeholder: string): string {
  const armed = equipArmedSlot === slot ? ' armed' : '';
  if (!id) return `<button class="cast-slot${armed}" data-slot="${slot}">${placeholder}</button>`;
  const color = runeColor(id);
  const nameTag = runeNameTag(id);
  return `<button class="cast-slot filled${armed}${nameTag ? ' named' : ''}" data-slot="${slot}" style="color:${color}; border-color:${color}">${runeLabel(id)}${nameTag}</button>`;
}

function renderCastLinePreview(): string {
  const line = currentEquippedLine();
  const parts = [slotChipHtml('concept', line.conceptId, 'Concept'), slotChipHtml('operator', line.operatorId, 'Operator')];
  if (needsSecondConcept()) parts.push(slotChipHtml('secondConcept', line.secondConceptId, 'Into'));
  const modChips = [...line.modifierIds]
    .map((id) => {
      const nameTag = runeNameTag(id);
      return `<span class="cast-slot filled${nameTag ? ' named' : ''}" style="color:${runeColor(id)}; border-color:${runeColor(id)}">${runeLabel(id)}${nameTag}</span>`;
    })
    .join('');
  return `<div class="cast-slots">${parts.join('')}${modChips}</div>`;
}

function describeCastLine(): string {
  const line = currentEquippedLine();
  const label = (id: string) => (getDisplayMode() === 'symbols' ? RUNE_SYMBOL[id] ?? '?' : ALL_RUNES.find((r) => r.id === id)?.name ?? id);
  const concept = line.conceptId ? label(line.conceptId) : '';
  const operator = line.operatorId ? label(line.operatorId) : '';
  const second = needsSecondConcept() && line.secondConceptId ? ` -> ${label(line.secondConceptId)}` : '';
  const mods = [...line.modifierIds].map(label);
  return `${concept} ${operator}${second}${mods.length ? ` (${mods.join(', ')})` : ''}`;
}

// Compact per-quadrant summary -- unlike renderCastLinePreview (which shows
// armed-slot placeholders for the line currently being edited), this only
// ever shows what's actually set, or "Empty", since a quadrant you're not
// editing has no "next slot to fill" to highlight.
function quadrantChipsHtml(line: CastLine): string {
  const ids = [line.conceptId, line.operatorId, line.secondConceptId, ...line.modifierIds].filter((id): id is string => !!id);
  if (ids.length === 0) return '<span class="muted small">Empty</span>';
  return `<div class="cast-slots">${ids
    .map((id) => {
      const nameTag = runeNameTag(id);
      return `<span class="cast-slot filled${nameTag ? ' named' : ''}" style="color:${runeColor(id)}; border-color:${runeColor(id)}">${runeLabel(id)}${nameTag}</span>`;
    })
    .join('')}</div>`;
}

// Menu is exempt on purpose (see the "Hide HUD buttons" checkbox in Settings)
// -- it's the one way back into this very panel, so it can never disappear
// even if a player binds a key to it.
function shouldHideButton(action: ActionId): boolean {
  return action !== 'menu' && getHideMappedButtons() && !!getKeybinding(action);
}

// A plain-text " (e)" suffix for a bound action, lowercase to match how a
// key is actually pressed rather than the Settings panel's own display
// convention (which shows "E") -- '' when nothing's bound, so unbound
// buttons read exactly as before.
function keybindSuffix(action: ActionId): string {
  const code = getKeybinding(action);
  return code ? ` (${formatKeyCode(code).toLowerCase()})` : '';
}

// Same hint, as a small corner badge for buttons built from HTML rather than
// plain text (the quadrant slots, whose content is the equipped rune chips).
function keybindHintHtml(action: ActionId): string {
  const code = getKeybinding(action);
  return code ? `<span class="keybind-hint">${formatKeyCode(code).toLowerCase()}</span>` : '';
}

// `applyHiding` only ever applies to the always-visible main-HUD grid -- the
// Equip panel's own picker always shows all 4 quadrants regardless of the
// "Hide HUD buttons" setting, since a player editing loadouts needs to be
// able to see and click every quadrant there even if they've keybound and
// hidden some of them on the main HUD.
function renderQuadrantButtonsHtml(applyHiding: boolean): string {
  return equippedSlots
    .map((line, i) => {
      const action = `quadrant${i + 1}` as ActionId;
      const hiddenClass = applyHiding && shouldHideButton(action) ? ' hidden' : '';
      return `<button class="quadrant-slot${i === activeSlotIndex ? ' active' : ''}${hiddenClass}" data-slot="${i}">${keybindHintHtml(action)}${quadrantChipsHtml(line)}</button>`;
    })
    .join('');
}

// Wires whichever quadrant grid was just rendered -- the always-visible HUD
// grid and the Equip panel's own picker are separate DOM instances of the
// same data (the panel's content is torn down and rebuilt on every render),
// so each needs its own click wiring after being (re)inserted.
function wireQuadrantButtons(container: ParentNode | null): void {
  container?.querySelectorAll<HTMLButtonElement>('.quadrant-slot').forEach((btn) => {
    btn.addEventListener('click', () => {
      activeSlotIndex = Number(btn.dataset.slot);
      equipArmedSlot = 'concept';
      refreshQuadrantUI();
    });
  });
}

// Always-visible HUD readout of all four loadouts, with the active one
// highlighted -- lives outside any panel so it's legible whether or not the
// Equip panel is open.
function renderQuadrantGrid(): void {
  const grid = document.getElementById('quadrant-grid');
  if (!grid) return;
  grid.innerHTML = renderQuadrantButtonsHtml(true);
  wireQuadrantButtons(grid);
}

// The HUD grid and the (possibly open) Equip panel both show live state
// derived from equippedSlots/activeSlotIndex -- refreshing both after any
// change is simplest and harmless even when the panel isn't open (its content
// just re-renders hidden).
function refreshQuadrantUI(): void {
  renderQuadrantGrid();
  renderEquipPanel();
}

// The Interact/Mark/Cast buttons live in the main HUD, not inside a modal --
// they need to work while the player keeps moving/playing, which a blocking
// .ui-panel (and the movement-pausing isMenuOpen() it implies) would defeat.
function renderActionButtons(): void {
  const label = document.getElementById('faced-target-label');
  if (label) {
    label.textContent = facedTarget ? `Facing: ${facedTarget.name}` : 'Facing: nothing in reach';
    label.classList.toggle('hidden', !getShowFacingDebug());
  }

  const interactBtn = document.getElementById('interact-action-button') as HTMLButtonElement | null;
  if (interactBtn) {
    if (facedTarget?.interact) {
      interactBtn.textContent = facedTarget.interact.label + keybindSuffix('interact');
      interactBtn.disabled = false;
    } else {
      interactBtn.textContent = 'Interact' + keybindSuffix('interact');
      interactBtn.disabled = true;
    }
    interactBtn.classList.toggle('hidden', shouldHideButton('interact'));
  }

  const markBtn = document.getElementById('mark-action-button') as HTMLButtonElement | null;
  if (markBtn) {
    if (facedTarget?.linkable) {
      markBtn.textContent = (getLinkedSource() === facedTarget.id ? 'Unmark as Source' : 'Mark as Source') + keybindSuffix('mark');
      markBtn.disabled = false;
    } else {
      markBtn.textContent = 'Mark' + keybindSuffix('mark');
      markBtn.disabled = true;
    }
    markBtn.classList.toggle('hidden', shouldHideButton('mark'));
  }

  const equipBtn = document.getElementById('equip-button') as HTMLButtonElement | null;
  if (equipBtn) {
    equipBtn.textContent = 'Equip' + keybindSuffix('equip');
    equipBtn.classList.toggle('hidden', shouldHideButton('equip'));
  }
  const castBtn = document.getElementById('cast-action-button') as HTMLButtonElement | null;
  if (castBtn) {
    castBtn.textContent = 'Cast' + keybindSuffix('cast');
    castBtn.classList.toggle('hidden', shouldHideButton('cast'));
  }
  const menuBtn = document.getElementById('menu-button') as HTMLButtonElement | null;
  if (menuBtn) menuBtn.textContent = 'Menu' + keybindSuffix('menu');
}

// --- Equip panel -----------------------------------------------------------------

function renderEquipPanel(): void {
  const content = document.getElementById('equip-panel-content');
  if (!content) return;
  const line = currentEquippedLine();

  content.innerHTML = `
    <p class="muted small">Editing quadrant ${activeSlotIndex + 1}:</p>
    <div class="quadrant-grid quadrant-grid-equip">${renderQuadrantButtonsHtml(false)}</div>
    ${renderCastLinePreview()}
    ${renderRuneBoard()}
    <div class="cast-actions">
      <button class="ui-button" id="equip-clear-button">Clear</button>
    </div>`;

  wireQuadrantButtons(content.querySelector('.quadrant-grid-equip'));

  content.querySelectorAll<HTMLButtonElement>('.cast-slot[data-slot]').forEach((el) => {
    el.addEventListener('click', () => {
      equipArmedSlot = el.dataset.slot as CastSlot;
      renderEquipPanel();
    });
  });

  content.querySelectorAll<HTMLButtonElement>('.rune-board-chip').forEach((btn) => {
    const runeId = btn.dataset.runeId!;
    const isConcept = CONCEPT_LIST.some((c) => c.id === runeId);
    const isOperator = OPERATOR_LIST.some((o) => o.id === runeId);

    if (!isConcept && !isOperator) {
      // Modifiers are independently toggleable, not gated by the armed slot.
      if (line.modifierIds.has(runeId)) btn.classList.add('selected');
      btn.addEventListener('click', () => {
        if (line.modifierIds.has(runeId)) line.modifierIds.delete(runeId);
        else line.modifierIds.add(runeId);
        refreshQuadrantUI();
      });
      return;
    }

    if (isConcept && (line.conceptId === runeId || line.secondConceptId === runeId)) btn.classList.add('selected');
    if (isOperator && line.operatorId === runeId) btn.classList.add('selected');

    btn.addEventListener('click', () => {
      if (isConcept && equipArmedSlot === 'concept') {
        line.conceptId = runeId;
        line.modifierIds.clear();
        equipArmedSlot = 'operator';
      } else if (isConcept && equipArmedSlot === 'secondConcept') {
        line.secondConceptId = runeId;
      } else if (isOperator && equipArmedSlot === 'operator') {
        line.operatorId = runeId;
        line.modifierIds.clear();
        if (operatorRequiresSecondConcept(runeId)) equipArmedSlot = 'secondConcept';
      }
      // Mismatched category-vs-armed-slot clicks are ignored, same as rune-lab's Play tab.
      refreshQuadrantUI();
    });
  });

  content.querySelector('#equip-clear-button')?.addEventListener('click', () => {
    equippedSlots[activeSlotIndex] = emptyCastLine();
    equipArmedSlot = 'concept';
    refreshQuadrantUI();
  });
}

// --- Interact panel ----------------------------------------------------------------
// A flat, one-shot flavor-text display -- no "choose" step, since Interact is
// now its own dedicated button rather than one option among several inside a
// per-object menu.

function renderInteractPanel(): void {
  const title = document.getElementById('interact-panel-title');
  if (title && facedTarget) title.innerHTML = escapeHtml(facedTarget.name).replace(/^([α-ω])/, '<span class="rune-symbol-glyph">$1</span>');
  const content = document.getElementById('interact-panel-content');
  if (content) {
    content.innerHTML = resolvedFlavorText
      .split('\n')
      .map((line) =>
        line.startsWith(GRANT_LINE_MARKER)
          ? `<p class="object-flavor-text grant-line">${line.slice(GRANT_LINE_MARKER.length)}</p>`
          : `<p class="object-flavor-text">${line}</p>`,
      )
      .join('');
    if (facedTarget?.interact?.showFeedbackForm) {
      trackEvent('reached_ending', { objectId: facedTarget.id, roomId: getCurrentRoomId() });
      renderFeedbackForm(content);
    }
  }
}

// Appended below the Portal's flavor text (see ObjectInteract.showFeedbackForm) --
// a generic hook, not hardcoded to that one object, same shape as every other
// give/place mechanic above. POSTs to /api/feedback (see feedbackApi.ts);
// playerId is the same anonymous id analytics.ts already generates, so a
// submission can be cross-referenced against that person's analytics trail.
function renderFeedbackForm(content: HTMLElement): void {
  const form = document.createElement('div');
  form.id = 'feedback-form-container';
  form.innerHTML = `
    <h3>Got a minute? Tell me what you thought.</h3>
    <div class="settings-group">
      <span class="settings-label">Overall, how did it feel?</span>
      <div class="settings-options feedback-rating-row">
        ${[1, 2, 3, 4, 5].map((n) => `<label><input type="radio" name="feedback-rating" value="${n}" /> ${n}</label>`).join('')}
      </div>
    </div>
    <label class="feedback-field-label" for="feedback-liked">What worked?</label>
    <textarea id="feedback-liked" class="feedback-form-textarea" rows="2"></textarea>
    <label class="feedback-field-label" for="feedback-confusing">What was confusing, or where did you get stuck?</label>
    <textarea id="feedback-confusing" class="feedback-form-textarea" rows="2"></textarea>
    <label class="feedback-field-label" for="feedback-other">Anything else?</label>
    <textarea id="feedback-other" class="feedback-form-textarea" rows="2"></textarea>
    <label class="feedback-field-label" for="feedback-email">Email (optional, only if you're open to follow-up)</label>
    <input type="email" id="feedback-email" class="feedback-form-input" />
    <button id="feedback-submit-button" class="ui-button">Submit Feedback</button>
    <p id="feedback-status" class="muted small"></p>`;
  content.appendChild(form);

  form.querySelector('#feedback-submit-button')?.addEventListener('click', async () => {
    const statusEl = form.querySelector('#feedback-status');
    const submitBtn = form.querySelector<HTMLButtonElement>('#feedback-submit-button');
    const ratingInput = form.querySelector<HTMLInputElement>('input[name="feedback-rating"]:checked');
    const liked = form.querySelector<HTMLTextAreaElement>('#feedback-liked')?.value ?? '';
    const confusing = form.querySelector<HTMLTextAreaElement>('#feedback-confusing')?.value ?? '';
    const other = form.querySelector<HTMLTextAreaElement>('#feedback-other')?.value ?? '';
    const email = form.querySelector<HTMLInputElement>('#feedback-email')?.value ?? '';

    submitBtn?.setAttribute('disabled', 'true');
    if (statusEl) statusEl.textContent = 'Sending...';
    try {
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          playerId: getAnalyticsPlayerId(),
          rating: ratingInput ? Number(ratingInput.value) : null,
          liked,
          confusing,
          other,
          email,
        }),
      });
      if (!response.ok) throw new Error(String(response.status));
      form.innerHTML = '<p class="muted small">Thanks -- feedback sent.</p>';
    } catch {
      if (statusEl) statusEl.textContent = "Couldn't send that -- check your connection and try again.";
      submitBtn?.removeAttribute('disabled');
    }
  });
}

function formatResultText(result: CastResult, input: CastInput, targetName: string): string {
  if (getCastTextMode() === 'narrative') return narrateCast(input, targetName);
  const parts = [`Dimensional signature: ${result.dimensionSummary}.`, `Cost: ${result.costLabel} (${result.costTier}).`, ...result.effect];
  if (result.warnings.length) parts.push(...result.warnings.map((w) => `Warning: ${w}`));
  return parts.join(' ');
}

function formatResultHtml(result: CastResult, input: CastInput, targetName: string): string {
  if (getCastTextMode() === 'narrative') return `<p>${narrateCast(input, targetName)}</p>`;
  const effectHtml = result.effect.map((line) => `<p>${line}</p>`).join('');
  const warningsHtml = result.warnings.length
    ? `<div class="cast-warnings">${result.warnings.map((w) => `<p>&#9888; ${w}</p>`).join('')}</div>`
    : '';
  return `
    <p><strong>Dimensional signature:</strong> ${result.dimensionSummary}</p>
    <p><strong>Cost:</strong> <span style="color:${TIER_COLOR[result.costTier]}">${result.costLabel} -- ${result.costTier}</span></p>
    <div class="cast-effect">${effectHtml}</div>
    ${warningsHtml}`;
}

// The cast boundary. Every one of the eight effect bridges
// (game/src/*Effect.ts) is a synchronous call out to GameScene, so a throw
// anywhere in a cast handler -- or in the resolve/narration chain here --
// unwinds through exactly this frame. Guarding here therefore covers the whole
// dispatch without eight separate try/catches, each of which would also need
// its own fabricated fallback result to satisfy the bridge's return type.
//
// The world may be left mid-mutation by a throw partway through an effect.
// That is worth saying plainly: this boundary keeps the game running and gets
// the crash reported, it does not make the cast atomic. The player's recovery
// is the room restart; ours is the report.
function performCast(): void {
  try {
    performCastUnguarded();
  } catch (err) {
    reportError(err, 'cast', { ...currentEquippedLine(), faced: facedTarget?.id ?? null });
    showErrorNotice('Something went wrong with that cast. It may not have finished — restarting the room from the Menu is the safe move.');
  }
}

// Fired by the Cast button -- no panel content element to render into anymore,
// since Cast is a non-blocking HUD action, not a modal step. The target is
// resolved fresh every call from whatever's currently faced (see
// updateFacedTarget), falling back to the caster exactly like an unmarked
// Transfer source already did.
function performCastUnguarded(): void {
  const resultEl = document.getElementById('cast-result-hud');
  const showMessage = (text: string) => {
    if (resultEl) resultEl.innerHTML = `<p class="muted small">${text}</p>`;
  };

  const line = currentEquippedLine();
  if (!line.conceptId || !line.operatorId) {
    showMessage('Equip at least a concept and an operator first.');
    return;
  }
  if (needsSecondConcept() && !line.secondConceptId) {
    const opName = ALL_RUNES.find((r) => r.id === line.operatorId)?.name ?? 'This operator';
    showMessage(`${opName} needs a second concept to convert into.`);
    return;
  }

  const castTargetId = facedTarget?.id ?? 'self';
  const castTargetName = facedTarget?.name ?? 'Yourself';

  const input: CastInput = {
    conceptId: line.conceptId as ConceptId,
    operatorId: line.operatorId as OperatorId,
    secondConceptId: needsSecondConcept() ? (line.secondConceptId as ConceptId) : undefined,
    modifierIds: [...line.modifierIds],
  };
  // The first point at which the cast is definitely going to happen -- every
  // "you haven't equipped enough to cast anything" return is above this line, and
  // no effect has run below it yet. Both properties matter: announcing earlier
  // would snapshot a turn for a cast that never occurred, and announcing later
  // would snapshot a world the cast had already changed.
  triggerCastBegan();
  const result = resolve(input);
  trackEvent('cast', {
    conceptId: input.conceptId,
    operatorId: input.operatorId,
    secondConceptId: input.secondConceptId ?? null,
    modifierIds: input.modifierIds,
    targetType: castTargetId === 'self' ? 'self' : 'object',
    roomId: getCurrentRoomId(),
  });

  if (input.operatorId === 'measure') {
    const radial = input.modifierIds.includes('radial');
    const { scale } = magnitudeScale(input.modifierIds);
    const radiusTiles = scale === 1 ? 3 : scale === 10 ? 6 : 10;
    triggerRadialMeasure({ conceptId: input.conceptId, radial, radiusTiles });
  }

  // A Transfer cast with a marked source acts on the *world*, not just the text --
  // it moves a real quantity between two objects (see transferEffect.ts/GameScene's
  // applyTransferEffect), which is how something like a heat source actually melts
  // an ice-blocked passage rather than that being one hardcoded combination. An
  // unmarked source defaults to the caster (03_Rune_Language.md's proposed locus
  // default) -- sloppiness is never free, it's just paid for out of your own
  // reserve (thermalState for Energy, Charge for Charge -- never Mana, which
  // only ever pays a cast's cost, not what it moves) instead of a proper
  // source's.
  // Named effectNote, not transferNote -- Project (below) has no target object
  // and no source/target concept at all, so this note now covers three
  // operators' worth of "what actually happened in the world," not just Transfer's.
  // Which effect this expression invokes is the simulation's decision, not this
  // function's (see sim/cast.ts) -- what stays here is the narration for each
  // outcome, which is the game's own voice and nothing the editor wants. The
  // branch conditions below therefore read the decided action rather than
  // re-deriving it from the concept/operator/modifiers.
  // Recorded from the same three fields the request is built out of, and before
  // the cast runs: `SimCommand`'s `cast` carries the expression, not the target,
  // because a replay re-derives the faced object from world state exactly as the
  // game does (see `facedObjectId`). The marked source is already in the
  // recording as its own `mark` command.
  recordCommand({ kind: 'cast', conceptId: input.conceptId, operatorId: input.operatorId, modifierIds: input.modifierIds });
  const action = castAction({
    conceptId: input.conceptId,
    operatorId: input.operatorId,
    modifierIds: input.modifierIds,
    targetObjectId: castTargetId,
    sourceObjectId: getLinkedSource() ?? 'self',
  });

  let effectNote = '';
  if (action.kind === 'momentum') {
    // Momentum's Transfer is a physical push, not a properties-bag mutation --
    // see momentumEffect.ts. Always self-sourced (there's no marked-source
    // concept for "borrow someone else's momentum"), so this bypasses the
    // marked-source logic below entirely.
    const effect = triggerMomentumEffect(action.event);
    if (effect.applied) {
      effectNote =
        effect.tilesMoved > 0
          ? `You shove ${castTargetName}, and the recoil sends you stumbling back.`
          : `You strain against ${castTargetName}, but you don't have the strength left to move it.`;
      if (effect.healthCost) effectNote += ' The effort costs you dearly.';
    }
  } else if (action.kind === 'transfer') {
    // Target can legitimately be 'self' too now, not just a faced object --
    // that's how the player actually charges themselves up from a marked
    // object (a Charged Ward Project discharge has to come from *somewhere*
    // real; see 12_Mechanics_World_Interaction.md). Momentum's push effect above
    // already carved out its own real-target-only path, and momentum+self
    // falls through here harmlessly: no object stores a `properties.momentum`
    // reserve, so at worst this moves a real 0 into the caster's own
    // (nonexistent) Momentum reserve. Source-into-itself never reaches here at
    // all -- castAction returns `none` for it.
    const { sourceObjectId: sourceId, reversed } = action.event;
    const effect = triggerTransferEffect(action.event);
    if (effect.applied) {
      // A source marked in a different room isn't a real object here (Transfer
      // only ever acts within the current room), so GameScene silently falls
      // back to the caster rather than failing the cast -- effect.sourceWasSelf
      // reflects what *actually* happened, not this function's stale guess
      // from sourceId, which only knows the mark, not whether it was usable.
      if (sourceId !== 'self' && effect.sourceWasSelf) {
        clearLinkedSource();
        updateLinkIndicator();
      }
      const sourceName = effect.sourceWasSelf ? 'yourself' : getLinkedSourceName() ?? 'the source';
      const conceptLabel = runeLabel(input.conceptId);
      if (effect.relocatedSource) {
        // Not a trickle -- the source itself moved. Worth its own line, and no
        // melt/spend/cost suffixes, because none of them happened: this is the
        // one Transfer whose consequence is permanent, since everything else
        // the verb does relaxes back toward the network's equilibrium and this
        // *is* the equilibrium moving. See applyTransferEffect.
        effectNote = `Whatever makes ${effect.relocatedSource.sourceName} a wellspring comes loose all at once, and settles into ${effect.relocatedSource.targetName}. It draws from there now.`;
      } else {
        effectNote = reversed
          ? `You draw ${conceptLabel} out of ${castTargetName} and into ${sourceName}.`
          : `You channel ${conceptLabel} from ${sourceName} into ${castTargetName}.`;
        // A Bind·Entropy link displaces a Transfer's loci, so the cast can land
        // somewhere other than where it was aimed (see sim/entropyBind.ts).
        // Said out loud, because the alternative is a line that names two
        // objects neither of which changed -- the player would read a working
        // cast as a broken one. *That* the link redirects is still theirs to
        // discover: nothing here says so until they have already bound a pair.
        if (effect.displacedTargetName) effectNote += ` The binding takes it elsewhere -- it settles on ${effect.displacedTargetName} instead.`;
        if (effect.displacedSourceName) effectNote += ` The binding answers for the draw -- it comes out of ${effect.displacedSourceName} instead.`;
        if (effect.meltedTargetName) effectNote += ` ${effect.meltedTargetName} gives way completely.`;
        if (effect.meltedSourceName) effectNote += ` ${effect.meltedSourceName} is spent.`;
        // A state ladder crossing is a consequence of the cast exactly as a melt
        // is, and says its own sentence rather than borrowing the melt's -- a
        // candle that goes out has not given way completely.
        for (const note of effect.stateNotes ?? []) effectNote += ` ${note}`;
        if (effect.healthCost) effectNote += ' The draw outpaces what you have to give -- it costs you dearly.';
      }
    }
  } else if (action.kind === 'project' && input.conceptId === 'momentum') {
    // Project·Momentum: a force-push beam/field, or (Reverse) a tractor beam
    // that pulls instead -- see GameScene's applyMomentumProject for how this
    // differs from Transfer·Momentum's single-target shove-and-recoil.
    const { radial, reversed } = action.event;
    const effect = triggerProjectEffect(action.event);
    const verb = reversed ? 'pull' : 'blast';
    const shape = radial ? 'outward around you' : 'straight ahead';
    if (effect.insufficientReserve) {
      // v1: a Project cast fires at full strength or not at all -- see
      // GameScene.ts's applyProjectEffect and 12_Mechanics_World_Interaction.md.
      effectNote = `You don't have enough Energy left to project a force ${shape} at all -- nothing happens.`;
    } else if (!effect.applied) {
      effectNote = `You unleash a force ${verb} ${shape}, but nothing pushable is within reach.`;
    } else if (!effect.movedNames?.length) {
      effectNote = `You unleash a force ${verb} ${shape}. ${effect.hitNames.join(', ')} ${effect.hitNames.length === 1 ? 'catches' : 'catch'} it, but ${effect.hitNames.length === 1 ? "doesn't" : "don't"} budge.`;
    } else {
      effectNote = reversed
        ? `You unleash a tractor pull ${shape}. ${effect.movedNames.join(', ')} ${effect.movedNames.length === 1 ? 'lurches' : 'lurch'} toward you.`
        : `You unleash a force blast ${shape}. ${effect.movedNames.join(', ')} ${effect.movedNames.length === 1 ? 'goes' : 'go'} flying.`;
    }
  } else if (action.kind === 'project') {
    // Project ignores castTargetId entirely -- it has no single target, only
    // whatever's within range of the caster's own position/facing (see
    // GameScene's applyProjectEffect), regardless of what's currently faced.
    // This is the operator that proved a target object was never required.
    const { radial, reversed } = action.event;
    const effect = triggerProjectEffect(action.event);
    const conceptLabel = runeLabel(input.conceptId);
    // Energy·Reverse is a cold-ray, not a second heat-ray (see
    // applyProjectEffect's own comment) -- narrated distinctly from the
    // plain direction, the same way Project·Momentum's own Reverse already
    // gets a "tractor pull" narration instead of reusing "force blast".
    const isColdRay = input.conceptId === 'energy' && reversed;
    if (effect.noReserve) {
      // The caster holds no reserve of this concept to project from at all --
      // Entropy, Mass, Space and Time (see casts.ts's projectReserveFor).
      // Deliberately says what the caster *feels* rather than naming the rule:
      // that a projection needs something in you to be made of is exactly the
      // kind of thing this world wants inferred, not explained. Distinct from
      // the "not enough left" line below, which is a quantity problem.
      effectNote = `You shape the projection and it finds nothing in you to carry — ${conceptLabel} is not yours to throw.`;
    } else if (effect.insufficientReserve) {
      // v1: a Project cast fires at full strength or not at all -- no
      // silent partial delivery, and no bolt/field FX either (see
      // GameScene.ts's applyProjectEffect and 12_Mechanics_World_Interaction.md).
      const reserveLabel = input.conceptId === 'charge' ? 'Charge' : 'Energy';
      const verb = isColdRay ? 'chill' : 'project';
      effectNote = `You don't have enough ${reserveLabel} left to ${verb} ${conceptLabel} at all -- nothing happens.`;
    } else if (!effect.applied) {
      effectNote = isColdRay
        ? radial
          ? `A wave of cold rolls outward, but nothing within reach carries it.`
          : `A chilling ray lances out, but nothing within reach carries it.`
        : radial
          ? `You project ${conceptLabel} outward, but nothing within reach carries it.`
          : `You project ${conceptLabel} straight ahead, but nothing within reach carries it.`;
    } else {
      const shape = radial ? 'outward around you' : 'straight ahead';
      const plural = effect.hitNames.length === 1 ? 'thing' : 'things';
      effectNote = isColdRay
        ? `A wave of cold radiates ${shape}. ${effect.hitNames.length} ${plural} within reach: ${effect.hitNames.join(', ')}.`
        : `You project ${conceptLabel} ${shape}. ${effect.hitNames.length} ${plural} within reach: ${effect.hitNames.join(', ')}.`;
      if (effect.meltedNames.length) effectNote += ` ${effect.meltedNames.join(' and ')} gives way completely.`;
      for (const note of effect.stateNotes ?? []) effectNote += ` ${note}`;
    }
  } else if (action.kind === 'separate') {
    // Structural, not a quantity transfer -- splitting/merging conserves the
    // total mass either way, so this has no Energy cost the way Transfer/
    // Momentum/Project all do. Reverse flips split into merge, the same
    // context-dependent-meaning shape it already has under Transfer/Entropy.
    const { reversed } = action.event;
    const effect = triggerSeparateEffect(action.event);
    if (effect.applied) {
      effectNote = reversed
        ? `${effect.targetName} fuses with its neighbor into a single ${effect.resultName}.`
        : `${effect.targetName} splits into two ${effect.resultName}s.`;
    } else if (effect.failureReason === 'too-small-to-split') {
      effectNote = `${effect.targetName} is too small to split any further.`;
    } else if (effect.failureReason === 'no-room-to-split') {
      effectNote = `${effect.targetName} has nowhere to send the second piece.`;
    } else if (effect.failureReason === 'already-whole') {
      effectNote = `${effect.targetName} has nothing left to merge into.`;
    } else if (effect.failureReason === 'no-partner-to-merge') {
      effectNote = `Nothing the same size sits beside ${effect.targetName} to rejoin with.`;
    } else if (effect.failureReason === 'warded') {
      effectNote = `${effect.targetName} doesn't respond -- whatever's inscribed into it isn't letting Mass take hold.`;
    }
  } else if (action.kind === 'bind') {
    // Couples the target's own movement to the marked source (or the caster)
    // from here on -- see rune-lab/src/sim/casts.ts's applyBindEffect and
    // propagateBoundMovement. Structural, not a quantity transfer, so (like
    // Separate·Mass) this has no Energy cost and ignores magnitude entirely.
    const { mirrored, sourceObjectId: boundSourceId } = action.event;
    const effect = triggerBindEffect(action.event);
    if (effect.applied) {
      // A source marked in a different room isn't a real object here (same
      // rule Transfer's own marked source follows), so the effect silently
      // fell back to the caster -- clear the stale mark rather than leaving
      // the HUD pointing at a source the game isn't actually using.
      if (boundSourceId !== 'self' && effect.sourceWasSelf) {
        clearLinkedSource();
        updateLinkIndicator();
      }
      const possessive = effect.sourceWasSelf ? 'your' : `${getLinkedSourceName() ?? 'the source'}'s`;
      const asItMoves = effect.sourceWasSelf ? 'you move' : 'it moves';
      effectNote = mirrored
        ? `${effect.targetName} locks onto ${possessive} every step -- but moves to mirror it, not match it.`
        : `${effect.targetName} locks onto ${possessive} every step, moving exactly as ${asItMoves}.`;
    }
  } else if (action.kind === 'unbind') {
    // Releases an existing Bind·Momentum link -- Bind creates the coupling,
    // Separate undoes it, the same create/undo shape the pair already has
    // for Mass (fuse) vs. Mass·Reverse (remerge).
    const effect = triggerUnbindEffect(action.event);
    effectNote = effect.applied
      ? `${effect.targetName} shudders and goes still -- whatever held it to you lets go.`
      : `${effect.targetName} was never answering to you in the first place.`;
  } else if (action.kind === 'energy-bind') {
    // Thermal contact with the marked source (or the caster) -- see
    // rune-lab/src/sim/thermal.ts's solveThermalStep. Not a quantity
    // transfer at cast time the way Transfer is: nothing here moves an
    // amount directly, the link itself is what keeps closing the gap, every
    // tick, from here on -- casting it just settles once immediately so
    // there's something to see right away.
    const { sourceObjectId: linkedSourceId } = action.event;
    const effect = triggerEnergyBindEffect(action.event);
    if (effect.applied) {
      if (linkedSourceId !== 'self' && effect.sourceWasSelf) {
        clearLinkedSource();
        updateLinkIndicator();
      }
      const possessive = effect.sourceWasSelf ? 'your' : `${getLinkedSourceName() ?? 'the source'}'s`;
      effectNote = `${effect.targetName} settles into thermal contact with ${possessive} own warmth -- the two will keep drifting toward each other from here on.`;
    }
  } else if (action.kind === 'unbind-energy') {
    // Releases an existing Bind·Energy link -- Bind opens it, Separate
    // closes it, the same create/undo shape as Momentum and Mass.
    const effect = triggerUnbindEnergyEffect(action.event);
    effectNote = effect.applied
      ? `${effect.targetName} cools back to its own warmth, independent again.`
      : `${effect.targetName} was never in thermal contact with you in the first place.`;
  } else if (action.kind === 'charge-bind') {
    // A wire, with no wire -- see rune-lab/src/sim/types.ts's ChargeBindDef.
    // Same "nothing moves at cast time, the link is what keeps moving it"
    // shape Bind·Energy has; the narration deliberately does not say where
    // the Charge will end up, only that the two are now connected, since
    // watching which way it actually runs is the thing worth discovering.
    const { sourceObjectId: linkedSourceId } = action.event;
    const effect = triggerChargeBindEffect(action.event);
    if (effect.applied) {
      if (linkedSourceId !== 'self' && effect.sourceWasSelf) {
        clearLinkedSource();
        updateLinkIndicator();
      }
      const other = effect.sourceWasSelf ? 'you' : (getLinkedSourceName() ?? 'the source');
      effectNote = `The air between ${effect.targetName} and ${other} tightens into something that is not quite there -- and current begins to run along it. You can feel the warmth of it in your own hands, and it lasts exactly as long as the flow does.`;
    }
  } else if (action.kind === 'unbind-charge') {
    // Cuts an existing Bind·Charge wire -- Bind strings it, Separate cuts
    // it, the same create/undo shape as Momentum, Energy and Mass. Each side
    // keeps whatever Charge it is holding, which is what makes cutting the
    // wire at the right moment a real move rather than housekeeping.
    const effect = triggerUnbindChargeEffect(action.event);
    effectNote = effect.applied
      ? `The current stops. ${effect.targetName} keeps what it is holding, and the warmth in your hands fades.`
      : `${effect.targetName} was never wired to anything in the first place.`;
  } else if (action.kind === 'entropy-bind') {
    // A displacement -- see rune-lab/src/sim/types.ts's EntropyBindDef. Same
    // "nothing is promised at cast time" shape the other standing links have:
    // the narration says the two now stand in for each other, without saying
    // which way a given Transfer will run. Naming one end and watching the
    // ruin land on the other is the discovery, and spelling it out here would
    // spend it.
    //
    // One line, not two: Reverse selects nothing here (2026-08-16), so there is
    // no second reading for the player to have to tell apart.
    const { sourceObjectId: linkedSourceId } = action.event;
    const effect = triggerEntropyBindEffect(action.event);
    if (effect.applied) {
      if (linkedSourceId !== 'self' && effect.sourceWasSelf) {
        clearLinkedSource();
        updateLinkIndicator();
      }
      const other = effect.sourceWasSelf ? 'you' : (getLinkedSourceName() ?? 'the source');
      effectNote = !effect.coupled
        ? `The binding closes on ${effect.targetName} and ${other} and finds nothing on the far side to stand in its place. Ruin needs somewhere else to go.`
        : `${effect.targetName} and ${other} change places in the ledger of ruin. Reach for one and your hand closes on the other.`;
    }
  } else if (action.kind === 'unbind-entropy') {
    // Severs an existing Bind·Entropy coupling -- Bind strikes the ledger,
    // Separate closes it, the same create/undo shape as Momentum, Energy,
    // Charge and Mass. Each side keeps whatever it holds, which is what makes
    // cutting it at the right moment a real move rather than housekeeping.
    const effect = triggerUnbindEntropyEffect(action.event);
    effectNote = effect.applied
      ? `The ledger closes. ${effect.targetName} keeps whatever ruin it has come to, and answers for nothing else from here on.`
      : `${effect.targetName} was never answering for anything in the first place.`;
  }

  const description = describeCastLine();
  const involvedIds = [
    line.conceptId,
    line.operatorId,
    needsSecondConcept() ? line.secondConceptId : null,
    ...line.modifierIds,
  ].filter((id): id is string => !!id);

  const entry = `Cast on ${castTargetName}: ${description}. ${formatResultText(result, input, castTargetName)}${effectNote ? ` ${effectNote}` : ''}`;
  for (const id of involvedIds) addObservation(id, entry);

  const effectNoteHtml = effectNote ? `<p class="cast-transfer-note">${effectNote}</p>` : '';
  if (resultEl) {
    resultEl.innerHTML = `<p class="cast-result-heading">You cast ${description} on ${castTargetName}.</p>${formatResultHtml(result, input, castTargetName)}${effectNoteHtml}`;
  }
  // The faced object's own properties/existence may have just changed (a
  // melt, a threshold cross) -- GameScene's effect methods already call
  // pushFacedTarget() themselves for that; Interact/Mark availability still
  // needs a refresh here too in case marking state changed as a side effect
  // (clearLinkedSource() above).
  renderActionButtons();
  // And so may the marked object's *name*: a cast that moves Entropy renames
  // any relic it touches (sim/relic.ts), including one being used as the
  // source. getLinkedSourceName reads live, so this is all it takes -- but
  // without it the HUD would keep showing the name the object had when it was
  // marked, which is exactly the staleness the ladder exists to remove.
  updateLinkIndicator();
  // Last, after every effect has been applied and every readout refreshed: the
  // turn this cast cost. A turn's worth of ambient rules has to run against the
  // world the cast produced, not the one it started from.
  //
  // Reached even by a cast that achieved nothing -- an expression that resolved
  // to no action at all, a Project with too little reserve to fire. The caster
  // attempted it, and letting a failed attempt be free is what would turn a
  // room into something to brute-force rather than solve.
  triggerCastEnded();
}

// --- Door lock panel ---------------------------------------------------------------

// Doors with a keyItemId need the matching inventory item to unlock -- doors
// without one have no free unlock at all; something else in the room
// (ObjectInteract.unlocksDoorId) is the only real way through.
export function openDoorPanel(lockId: string, keyItemId?: string): void {
  renderDoorPanelContent(lockId, keyItemId);
  openPanel('door-panel');
}

function renderDoorPanelContent(lockId: string, keyItemId?: string): void {
  const content = document.getElementById('door-panel-content');
  if (!content) return;

  if (!isDoorLocked(lockId)) {
    content.innerHTML = '<p class="muted small">The door is unlocked.</p>';
    return;
  }

  if (keyItemId) {
    const keyDef = getItemDef(keyItemId);
    const keyName = keyDef?.name ?? 'key';

    if (!hasItem(keyItemId)) {
      content.innerHTML = `
        <p class="muted small">This door is locked.</p>
        <p class="muted small">It looks like it needs a specific key -- you don't have one yet.</p>`;
      return;
    }

    content.innerHTML = `
      <p class="muted small">This door is locked.</p>
      <p class="muted small">You have a ${keyName} that looks like it might fit.</p>
      <button class="ui-button" id="door-use-key-button">Use ${keyName}</button>
      <p class="muted small door-unlock-result"></p>`;

    content.querySelector('#door-use-key-button')?.addEventListener('click', () => {
      setDoorLocked(lockId, false);
      let text = `The ${keyName.toLowerCase()} turns with a satisfying click. The door unlocks.`;
      if (keyDef?.consumedOnUseText) {
        removeItem(keyItemId);
        text += ` ${keyDef.consumedOnUseText}`;
      }
      const resultEl = content.querySelector('.door-unlock-result');
      if (resultEl) resultEl.textContent = text;
      document.getElementById('door-use-key-button')?.setAttribute('disabled', 'true');
    });
    return;
  }

  // No keyItemId and still locked: nothing here opens it for free -- some
  // other object in the room (an ObjectInteract.unlocksDoorId, e.g. a lever)
  // is the only real way through. No input/button pair, since a free-text
  // "try anything" unlock would trivially bypass whatever's actually gating it.
  content.innerHTML = `
    <p class="muted small">This door is locked.</p>
    <p class="muted small">There's no keyhole, no lock to pick -- something else in this room must release it.</p>`;
}

// --- Inventory tab -----------------------------------------------------------------

function renderInventoryTab(): void {
  const content = document.getElementById('main-panel-content');
  if (!content) return;
  const items = getInventoryItems();
  content.innerHTML = items.length
    ? `<div class="inventory-list">${items
        .map((item) => `<div class="inventory-item"><strong>${item.name}</strong> -- ${item.description}</div>`)
        .join('')}</div>`
    : '<p class="muted small">Empty. Explore and interact with things to find items.</p>';
}

// --- Mana/Thermal/Health HUD bars -------------------------------------------------

export function updateStatsHud(): void {
  const manaFill = document.querySelector<HTMLElement>('.hud-bar-fill.mana');
  const thermalFill = document.querySelector<HTMLElement>('.hud-bar-fill.thermal');
  const healthFill = document.querySelector<HTMLElement>('.hud-bar-fill.health');
  if (manaFill) manaFill.style.width = `${(getMana() / getMaxMana()) * 100}%`;
  if (healthFill) healthFill.style.width = `${(getHealth() / getMaxHealth()) * 100}%`;
  if (thermalFill) {
    const value = getThermalState();
    // The bar is the only thing with a maximum. thermalState itself has had no
    // ceiling since 2026-08-09 (standing in something far hotter than the scale
    // really does take the reading past it), so the fill clamps at 100% rather
    // than overflowing its track -- the burning class below is what says "and
    // it is still going up".
    thermalFill.style.width = `${Math.min(100, (value / getMaxThermalState()) * 100)}%`;
    thermalFill.classList.toggle('thermal-frozen', value <= getThermalFrozenThreshold());
    thermalFill.classList.toggle('thermal-burning', value >= getThermalBurningThreshold());
  }
}

let currentRoomId = '';

// GameScene calls this on every room entry (see enterRoom) -- the label sits
// just below the Energy/Health bars, always reflecting whichever room the
// player is actually standing in right now. Also stashes the stable room id
// (distinct from the display name) for analytics.ts's room_enter event.
export function updateRoomName(id: string, name: string): void {
  currentRoomId = id;
  const label = document.getElementById('room-name-label');
  if (label) label.textContent = name;
}

export function getCurrentRoomId(): string {
  return currentRoomId;
}

// --- Linked-source HUD indicator -----------------------------------------------

// Exported (2026-08-12) for undo, which is the one caller outside this module.
// Every other path that changes the mark goes through objectLink.ts's setters and
// is covered by the calls already in this file; undo restores `markedSource` as
// part of a whole `WorldState` at once, so nothing tells this HUD that the mark it
// is displaying belongs to a world that has been rolled back.
export function updateLinkIndicator(): void {
  const el = document.getElementById('link-indicator');
  if (!el) return;
  const name = getLinkedSourceName();
  if (name) {
    el.textContent = `Source: ${name}`;
    el.classList.remove('hidden');
  } else {
    el.classList.add('hidden');
  }
}

// Shows/hides the D-pad based on the persisted movement-input-mode preference --
// called on init and whenever the Menu radio changes, same shape as the debug
// overlay's onDebugOverlayChange refresh.
function applyMovementInputMode(): void {
  document.getElementById('dpad')?.classList.toggle('hidden', getMovementInputMode() !== 'dpad');
}

// Shows or hides the Wait/Undo row, same shape as applyMovementInputMode above.
//
// Reads the **world**, not the setting, exactly like every other turn-mode gate
// in the game (see clockMode.ts). The distinction is not academic here: a
// playtest launched from the World Editor arrives carrying its own
// `PhysicsConfig`, so a room authored to be verified on the turn clock puts the
// world on the turn clock regardless of what this browser's menu remembers --
// and a HUD that consulted the menu would leave the tester without the two
// buttons the mode is about.
export function refreshTurnHud(): void {
  document.getElementById('turn-buttons-row')?.classList.toggle('hidden', !isTurnMode());
}

/** Enables or greys out the Undo button. Driven by undoHistory.ts, which is the
 * only thing that knows whether there is a turn to go back to -- at the top of a
 * room there is not, and a button that looks pressable and does nothing is worse
 * than one that says so. */
export function setUndoAvailable(available: boolean): void {
  const button = document.getElementById('undo-action-button') as HTMLButtonElement | null;
  if (button) button.disabled = !available;
}

// --- Title screen ------------------------------------------------------------------
// The very first thing shown on every page load, before the intro panel -- picks
// the two session-scoped settings (tutorial nudges, Developer Tools visibility) that
// don't belong on an in-progress New Game confirm since they're preferences, not
// progress. "New Game" here always does the same full reset the Settings-menu New
// Game confirm does (see performNewGameReset), since there's no Resume button on
// this screen to fall back to.

export function showTitle(): void {
  const tutorialCheckbox = document.getElementById('title-tutorial-checkbox') as HTMLInputElement | null;
  if (tutorialCheckbox) tutorialCheckbox.checked = getTutorialEnabled();
  openPanel('title-panel');
}

// --- Intro panel -----------------------------------------------------------------
// Shown once at the very start of a life (initial load, and again on New Game --
// "starting over" should mean waking up again), not on every ordinary re-entry to
// room0. GameScene triggers this explicitly rather than tying it to "entering
// room0" in general.

export function showIntro(): void {
  openPanel('intro-panel');
}

// --- Death panel -------------------------------------------------------------------
// Shown the moment GameScene sees a `player-died` sim event (Health reached 0 --
// see rune-lab/src/sim/player.ts). Reusing the same `.ui-panel` machinery as every
// other panel means this already gets keyboard navigation and blocks movement/
// casting input for free (isMenuOpen() only checks whether *some* panel is open).
// The "Try Again" button doesn't reset the world the way New Game does -- it fires
// triggerRoomRestart() (roomRestart.ts), which GameScene listens for and answers
// by rebuilding just the current room (restartRoom, sim/state.ts) and restoring
// Health/Energy/Charge to full; every other room, and everything carried or
// learned, survives untouched.

export function showDeathScreen(): void {
  openPanel('death-panel');
}

// --- Equip/Cast tutorial panels -----------------------------------------------------
// Split into two, shown in sequence rather than as one panel, so each teaches
// exactly one step instead of dumping the whole flow at once: the first fires
// the moment a player holds both Energy and Project (see resolveInteractText's
// giveRuneId branch) and only talks about Equip and the hotbar; the second
// fires a few ticks after the equip panel actually closes with both runes
// built into the same hotbar slot (see closePanel) and only then talks about
// Cast and the candle. Neither ever says what either rune actually *does* --
// room2's own sealed Guttering Candle (reachable only by Project, at range) is
// the intended first target, but this only explains the controls, never the
// puzzle.

function showEquipTutorial(): void {
  markSeenEquipTutorial();
  openPanel('equip-tutorial-panel');
}

function showCastTutorial(): void {
  markSeenCastTutorial();
  openPanel('cast-tutorial-panel');
}

// --- Modifier/Transfer tutorial panels ----------------------------------------------
// Two standalone popups, each independent of the Equip/Cast pair above and of
// each other: the first fires a couple of seconds after the player learns
// their first Modifier rune (see resolveInteractText's giveRuneId branch),
// explaining that a third rune category exists without naming what any
// specific modifier does; the second fires the same way after learning
// Transfer specifically, explaining Mark as Source mechanically (room4's own
// bookshelf-5 already covers it narratively, but this is the one rune whose
// controls aren't otherwise discoverable from Equip/Cast alone).

function showModifierTutorial(): void {
  markSeenModifierTutorial();
  openPanel('modifier-tutorial-panel');
}

function showTransferTutorial(): void {
  markSeenTransferTutorial();
  openPanel('transfer-tutorial-panel');
}

// --- Notebook tutorial panel ---------------------------------------------------------
// Fires a couple of seconds after the player takes the Warded Key out of
// room2's chest (see resolveInteractText's giveItemId branch) -- the first
// natural pause with two real runes already in hand, since reaching the
// chest requires having lit and melted the Guttering Candle with them.
// Points at the Runes tab's own "Name this rune" field, which otherwise has
// no onboarding of its own anywhere.

function showNotebookTutorial(): void {
  markSeenNotebookTutorial();
  openPanel('notebook-tutorial-panel');
}

// --- Developer Tools panel ---------------------------------------------------------
// Everything here is a debug/testing aid, not a player-facing setting -- kept in
// its own submenu specifically so it's easy to disable/hide access to as a whole
// later, separate from the always-visible Menu (New Game, Sprite, display prefs).

// Labels here are prose reminders, not routing -- data-room-jump/triggerRoomJump
// use the id alone, so a stale label here can never send a player to the wrong
// room, only *describe* it wrong. Kept in sync by hand with each buildRoomN's
// own name in GameScene.ts; last audited against the 2026-07-30 room graph
// renumbering (room7 "Quarry Yard" deleted, room8/9 renamed down one, new
// room9 inserted before the unchanged room10). room11 ("The Humming Floor")
// added 2026-08-04, spliced between room8 and room9. room14
// ("momentum-puzzle-1"), added 2026-08-06, is a user-authored gate/pad puzzle
// not yet wired into the main door graph -- listed here, like room13, purely
// because its own Playwright spec needs a way to reach it. room16 ("The Proving
// Floor"), added 2026-08-08, is the sandbox holding one of every mechanism at
// once; its only doors lead back into itself, so this list is the only way in.
const ROOM_JUMP_TARGETS: { id: string; label: string }[] = [
  { id: 'room0', label: 'Room 0 (The Waking Chamber)' },
  { id: 'room1', label: 'Room 1 (Antechamber)' },
  { id: 'room2', label: 'Room 2 (The Warded Study)' },
  { id: 'room3', label: 'Room 3 (The Charge Reliquary)' },
  { id: 'room4', label: 'Room 4 (The Ice Vault)' },
  { id: 'room5', label: 'Room 5 (The Mechanics Workshop)' },
  { id: 'room6', label: 'Room 6 (The Ruined Archive)' },
  { id: 'room7', label: 'Room 7 (The Gated Passage)' },
  { id: 'room8', label: 'Room 8 (The Conduit Crossing)' },
  { id: 'room9', label: 'Room 9 (The Sundered Aqueduct)' },
  { id: 'room10', label: 'Room 10 (The Waking Gate)' },
  { id: 'room11', label: 'Room 11 (The Humming Floor)' },
  { id: 'room13', label: 'Room 13 (The Crossing)' },
  { id: 'room14', label: 'Room 14 (momentum-puzzle-1)' },
  { id: 'room15', label: 'Room 15 (The Long Wire)' },
  { id: 'room16', label: 'Room 16 (The Proving Floor)' },
];

function renderRoomJumpButtons(): void {
  const container = document.getElementById('room-jump-buttons');
  if (!container) return;
  container.innerHTML = ROOM_JUMP_TARGETS.map((r) => `<button class="ui-button" data-room-jump="${r.id}">${r.label}</button>`).join('');
  container.querySelectorAll<HTMLButtonElement>('[data-room-jump]').forEach((btn) => {
    btn.addEventListener('click', () => triggerRoomJump(btn.dataset.roomJump!));
  });
}

function renderRevisionHistoryPanel(): void {
  const content = document.getElementById('revision-history-content');
  if (!content) return;
  content.innerHTML = REVISION_HISTORY.length
    ? `<ul class="observation-list">${REVISION_HISTORY.map(
        (r) => `<li><strong>#${r.number}</strong> (${r.date}) -- <a href="${r.url}" target="_blank" rel="noopener noreferrer">${r.title}</a></li>`,
      ).join('')}</ul>`
    : '<p class="muted small">No revision history recorded yet.</p>';
}

// --- Save / Resume ---------------------------------------------------------------
// One save slot, explicit only (no autosave) -- a snapshot of position/room state
// (from GameScene, via gameSave.ts's provider/applier) plus everything else that
// makes up progress (inventory, door locks, learned runes, notebook).

function saveGame(): void {
  const snapshot = getRoomSnapshot();
  if (!snapshot) return;
  const data: SaveData = {
    version: 1,
    savedAt: new Date().toISOString(),
    ...snapshot,
    inventory: getHeldItemIds(),
    collectedItemIds: getCollectedItemIds(),
    learnedRunes: getLearnedRuneIds(),
    doorLocks: getAllDoorLocks(),
    notebook: getNotebookState(),
    playerStats: getPlayerStatsSnapshot(),
    nextRockId: getWorld().nextRockId,
    nextVineId: getWorld().nextVineId,
  };
  writeSave(data);
  renderSaveLoadControls();
}

/** Puts the runtime-object id counters back, deriving a safe floor for a save
 * that predates them.
 *
 * The floor is one past the highest `rock-N` / `vine-N` actually present in the
 * restored world. That is not a guess at what the counter *was* -- a rock that
 * has since been merged away leaves a gap -- but it is the only thing that
 * matters, which is that no future id collides with one already in use. */
function restoreRuntimeIds(data: SaveData): void {
  const world = getWorld();
  const highest = (prefix: string): number => {
    let max = 0;
    for (const room of Object.values(world.rooms)) {
      for (const obj of room.objects) {
        if (!obj.id.startsWith(prefix)) continue;
        const n = Number(obj.id.slice(prefix.length));
        if (Number.isInteger(n) && n > max) max = n;
      }
    }
    return max + 1;
  };
  world.nextRockId = data.nextRockId ?? highest('rock-');
  world.nextVineId = data.nextVineId ?? highest('vine-');
}

function loadGame(): void {
  const data = readSave();
  if (!data) return;
  setHeldItemIds(data.inventory);
  // Saves from before this existed (see gameSave.ts's own comment) fall back
  // to whatever's still held -- not perfectly reconstructing history for a
  // key already consumed before that save, but no worse than the bug this
  // closes, and self-corrects the moment anything new is collected.
  setCollectedItemIds(data.collectedItemIds ?? data.inventory);
  setLearnedRuneIds(data.learnedRunes);
  setAllDoorLocks(data.doorLocks);
  setNotebookState(data.notebook);
  // Saves from before Energy/Health existed won't have this field.
  if (data.playerStats) setPlayerStatsSnapshot(data.playerStats);
  else resetPlayerStats();
  updateStatsHud();
  clearLinkedSource();
  updateLinkIndicator();
  // The equipped line was never part of the save format (same as the old
  // per-object castLine never was) -- a loaded game starts with an empty loadout.
  equippedSlots = Array.from({ length: QUADRANT_COUNT }, () => emptyCastLine());
  activeSlotIndex = 0;
  equipArmedSlot = 'concept';
  refreshQuadrantUI();
  applyRoomSnapshot(data);
  // After the rooms are back, so the fallback below can read them. A save from
  // before these were recorded gets a floor derived from what was actually
  // restored -- one past the highest id in use -- rather than 1, which would
  // hand the next split an id something already answers to.
  restoreRuntimeIds(data);
  closePanel('main-panel');
}

// --- New Game -----------------------------------------------------------------
// A two-step confirm (not a native confirm()) so it matches the rest of the DOM
// overlay's own styling instead of a jarring browser dialog.

// Shared by the Settings-menu New Game confirm and the title screen's New Game
// button -- both mean the exact same thing ("wipe progress, start over"), the
// title screen just has no Resume button to fall back to, so it never asks first.
function performNewGameReset(): void {
  resetDoors();
  resetInventory();
  resetNotebook();
  resetLearnedRunes();
  resetTutorialHints();
  resetPlayerStats();
  updateStatsHud();
  clearLinkedSource();
  updateLinkIndicator();
  // A leftover dev session (or a browser that just still has last time's
  // localStorage) shouldn't survive into a fresh life -- resets every actual
  // dev *behavior* toggle (overlays, signposts, cast text, display mode) the
  // same way unchecking "Enable developer tools" already does. Deliberately
  // leaves getDevToolsEnabled() itself alone (whether the Developer Tools
  // button is even visible) -- that's a session preference like the tutorial
  // checkbox, not progress, and title screen's own checkbox already covers it.
  resetDeveloperSettingsToPlayerDefaults();
  equippedSlots = Array.from({ length: QUADRANT_COUNT }, () => emptyCastLine());
  activeSlotIndex = 0;
  equipArmedSlot = 'concept';
  refreshQuadrantUI();
  triggerNewGame();
}

// Save/Resume and New Game share one row now (moved up beside each other per
// request) -- merged into a single render function so New Game's confirm step
// can swap just its own two buttons in place without disturbing Save/Resume,
// while still sharing the same status line above (repurposed as the confirm
// warning while it's showing). Title Screen sits in the same row: a plain
// `.ui-close` back to `title-panel` (see showTitle) with no confirm of its own,
// since it neither resets nor loses anything -- it's how a player mid-game
// reaches the Play Tutorial/Enable Developer Tools checkboxes without New
// Game's full wipe forcing itself on them just to flip a preference.
let confirmingNewGame = false;

function renderSaveLoadControls(): void {
  const controls = document.getElementById('save-load-controls');
  if (!controls) return;

  const save = hasSave() ? readSave() : null;
  const status = confirmingNewGame
    ? 'This resets your position, inventory, door locks, notebook, learned runes, Energy/Health, and the world (melted ice, etc). Are you sure?'
    : save
      ? `Last saved: ${new Date(save.savedAt).toLocaleString()}`
      : 'No save yet.';
  const newGameHtml = confirmingNewGame
    ? `<button class="ui-button" id="new-game-cancel">Cancel</button>
       <button class="ui-button" id="new-game-confirm">Yes, Restart</button>`
    : '<button class="ui-button" id="new-game-button">New Game</button>';

  controls.innerHTML = `
    <p class="muted small">${status}</p>
    <div class="cast-actions">
      <button class="ui-button" id="save-game-button">Save Game</button>
      <button class="ui-button" id="load-game-button" ${save ? '' : 'disabled'}>Resume</button>
      ${newGameHtml}
      <button class="ui-button" id="title-screen-button">Title Screen</button>
    </div>`;

  controls.querySelector('#save-game-button')?.addEventListener('click', saveGame);
  controls.querySelector('#load-game-button')?.addEventListener('click', loadGame);
  controls.querySelector('#new-game-button')?.addEventListener('click', () => {
    confirmingNewGame = true;
    renderSaveLoadControls();
  });
  controls.querySelector('#new-game-cancel')?.addEventListener('click', () => {
    confirmingNewGame = false;
    renderSaveLoadControls();
  });
  controls.querySelector('#new-game-confirm')?.addEventListener('click', () => {
    performNewGameReset();
    confirmingNewGame = false;
    renderSaveLoadControls();
    closePanel('main-panel');
  });
  // Rendered fresh each time (unlike the truly static `.ui-close` buttons in
  // index.html), so the generic one-time `.ui-close` wiring in initUI() never
  // sees this button -- wired explicitly here instead, same as the buttons
  // above it.
  controls.querySelector('#title-screen-button')?.addEventListener('click', showTitle);
}

// Disabling Developer Tools from the title screen should hand back a
// player-clean experience, not just hide the Developer Tools button --
// otherwise a player who briefly flipped it on (or a leftover dev session)
// keeps debug overlays/signposts/raw cast text/shorthand runes turned on
// with no way to see why. Every dev-only setting reverts to its
// player-facing default here, including showDevSignposts, whose own default
// is `true` (opt-out for developers) -- the player default is `false`, the
// opposite of that.
function resetDeveloperSettingsToPlayerDefaults(): void {
  setDevMode(false);
  setShowRuneDescriptions(false);
  setShowEnergyDebug(false);
  setShowChargeDebug(false);
  setShowConductivityDebug(false);
  setShowHealthDebug(false);
  setShowEntropyDebug(false);
  setShowFacingDebug(false);
  setShowGridCoords(false);
  setShowDevSignposts(false);
  setCastTextMode('narrative');
  setDisplayMode('symbols');

  // dev-tools-panel's checkboxes/radios are static markup wired once in
  // initUI(), not re-rendered on open -- resync their DOM state directly so
  // reopening the panel (after re-enabling Developer Tools) doesn't show
  // stale checked state left over from before the reset.
  const devModeToggle = document.getElementById('dev-mode-toggle') as HTMLInputElement | null;
  if (devModeToggle) devModeToggle.checked = false;
  const showDescriptionsToggle = document.getElementById('show-rune-descriptions-toggle') as HTMLInputElement | null;
  if (showDescriptionsToggle) showDescriptionsToggle.checked = false;
  const showEnergyDebugToggle = document.getElementById('show-energy-debug-toggle') as HTMLInputElement | null;
  if (showEnergyDebugToggle) showEnergyDebugToggle.checked = false;
  const showChargeDebugToggle = document.getElementById('show-charge-debug-toggle') as HTMLInputElement | null;
  if (showChargeDebugToggle) showChargeDebugToggle.checked = false;
  const showHealthDebugToggle = document.getElementById('show-health-debug-toggle') as HTMLInputElement | null;
  if (showHealthDebugToggle) showHealthDebugToggle.checked = false;
  const showEntropyDebugToggle = document.getElementById('show-entropy-debug-toggle') as HTMLInputElement | null;
  if (showEntropyDebugToggle) showEntropyDebugToggle.checked = false;
  const showConductivityDebugToggle = document.getElementById('show-conductivity-debug-toggle') as HTMLInputElement | null;
  if (showConductivityDebugToggle) showConductivityDebugToggle.checked = false;
  const showFacingDebugToggle = document.getElementById('show-facing-debug-toggle') as HTMLInputElement | null;
  if (showFacingDebugToggle) showFacingDebugToggle.checked = false;
  const showGridCoordsToggle = document.getElementById('show-grid-coords-toggle') as HTMLInputElement | null;
  if (showGridCoordsToggle) showGridCoordsToggle.checked = false;
  const showDevSignpostsToggle = document.getElementById('show-dev-signposts-toggle') as HTMLInputElement | null;
  if (showDevSignpostsToggle) showDevSignpostsToggle.checked = false;
  document.querySelectorAll<HTMLInputElement>('input[name="display-mode"]').forEach((radio) => {
    radio.checked = radio.value === 'symbols';
  });
  document.querySelectorAll<HTMLInputElement>('input[name="cast-text-mode"]').forEach((radio) => {
    radio.checked = radio.value === 'narrative';
  });

  renderActionButtons();
}

export function initUI(): void {
  // Menu/Notebook/Inventory used to be three separate buttons and panels;
  // condensed into one button opening one panel, with a tab bar selecting
  // which section renders. Always resets to the Settings tab on open, the
  // same tab the old dedicated Menu button landed on.
  document.getElementById('menu-button')?.addEventListener('click', () => {
    // Secret developer-tools unlock: 5 taps on this same button while the
    // panel is already open (not the tap that first opens it) flips
    // getDevToolsEnabled() on -- replaces the old, visibly-labeled title
    // screen checkbox so a playtester link doesn't hand out a "Developer
    // Tools" button by default. Resets after ~2s of no further taps so it
    // can't be triggered by accident across unrelated visits to the menu.
    if (openPanelId === 'main-panel' && !getDevToolsEnabled()) {
      menuSecretTapCount += 1;
      if (menuSecretTapTimer !== null) clearTimeout(menuSecretTapTimer);
      menuSecretTapTimer = setTimeout(() => {
        menuSecretTapCount = 0;
        menuSecretTapTimer = null;
      }, 2000);
      if (menuSecretTapCount >= 5) {
        menuSecretTapCount = 0;
        setDevToolsEnabled(true);
      }
    }
    activeMainTab = 'settings';
    openPanel('main-panel');
    renderMainPanelContent();
  });
  document.querySelectorAll<HTMLButtonElement>('#main-panel .notebook-tab-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      activeMainTab = btn.dataset.tab as MainTab;
      if (activeMainTab !== 'runes') selectedRuneId = null;
      renderMainPanelContent();
    });
  });
  document.getElementById('revision-history-button')?.addEventListener('click', () => {
    openPanel('revision-history-panel');
    renderRevisionHistoryPanel();
  });
  document.getElementById('equip-button')?.addEventListener('click', () => {
    openPanel('equip-panel');
    renderEquipPanel();
  });

  document.getElementById('interact-action-button')?.addEventListener('click', () => {
    if (isMenuOpen()) return;
    if (!facedTarget?.interact) return;
    markInteractedOnce();
    resolvedFlavorText = resolveInteractText(facedTarget.id, facedTarget.interact);
    renderInteractPanel();
    openPanel('interact-panel');
  });

  document.getElementById('mark-action-button')?.addEventListener('click', () => {
    if (isMenuOpen()) return;
    if (!facedTarget?.linkable) return;
    if (getLinkedSource() === facedTarget.id) clearLinkedSource();
    else setLinkedSource(facedTarget.id, facedTarget.name);
    updateLinkIndicator();
    renderActionButtons();
  });

  // pointerdown/up/leave/cancel (not click) so Measure can sample continuously
  // while held -- "For measure it happens as long as you hold the button" --
  // without needing any special handling for every other operator, which just
  // fires once on press the same as a plain click would.
  const castButton = document.getElementById('cast-action-button');
  const stopMeasureHold = () => {
    if (measureHoldInterval !== null) {
      clearInterval(measureHoldInterval);
      measureHoldInterval = null;
    }
  };
  castButton?.addEventListener('pointerdown', () => {
    if (isMenuOpen()) return;
    performCast();
    if (currentEquippedLine().operatorId === 'measure') {
      measureHoldInterval = setInterval(() => {
        if (isMenuOpen()) {
          stopMeasureHold();
          return;
        }
        performCast();
      }, 500);
    }
  });
  castButton?.addEventListener('pointerup', stopMeasureHold);
  castButton?.addEventListener('pointerleave', stopMeasureHold);
  castButton?.addEventListener('pointercancel', stopMeasureHold);

  // Keyboard shortcuts (Settings -> Keyboard shortcuts): a keydown either
  // captures into whatever action is currently "Set"-armed (see
  // renderKeybindingsControls) or fires its bound action. Cast specifically
  // mirrors the pointerdown/up wiring just above -- holding a bound Cast key
  // repeats Measure exactly like holding the button does -- every other
  // action is a plain equivalent to a single click, reusing that button's
  // own existing handler (and its disabled-state guard) rather than
  // duplicating any logic here.
  window.addEventListener('keydown', (e) => {
    if (listeningForAction) {
      e.preventDefault();
      if (e.code !== 'Escape') setKeybinding(listeningForAction, e.code);
      listeningForAction = null;
      renderKeybindingsControls();
      return;
    }
    // OS key-repeat would otherwise refire this on every repeat tick at
    // whatever cadence the OS uses -- Cast already has its own deliberate
    // 500ms repeat via the interval above, and nothing else should repeat at all.
    if (e.repeat) return;
    const target = e.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;

    // All four arrow keys navigate whichever panel is currently open,
    // highlighting one focused button at a time (see
    // movePanelFocusDirectional) -- Interact, below, activates it. Movement
    // already ignores these same codes while a panel is open (GameScene's
    // resolveDirection checks isMenuOpen()), so there's no conflict with
    // walking around.
    if (openPanelId && (e.code === 'ArrowUp' || e.code === 'ArrowDown' || e.code === 'ArrowLeft' || e.code === 'ArrowRight')) {
      e.preventDefault();
      const dir = e.code === 'ArrowUp' ? 'up' : e.code === 'ArrowDown' ? 'down' : e.code === 'ArrowLeft' ? 'left' : 'right';
      movePanelFocusDirectional(dir);
      return;
    }

    // Escape closes whatever panel/dialogue is open -- a universal "back out"
    // key independent of whatever's bound to Interact, since a real dialogue
    // box should never require hunting for the right button to leave it.
    if (openPanelId && e.code === 'Escape') {
      e.preventDefault();
      closePanel(openPanelId);
      return;
    }

    const action = findActionForCode(e.code);
    if (!action) return;

    if (action === 'cast') {
      if (isMenuOpen()) return;
      performCast();
      if (currentEquippedLine().operatorId === 'measure') {
        measureHoldInterval = setInterval(() => {
          if (isMenuOpen()) {
            stopMeasureHold();
            return;
          }
          performCast();
        }, 500);
      }
      return;
    }
    if (action === 'menu') document.getElementById('menu-button')?.click();
    else if (action === 'equip') document.getElementById('equip-button')?.click();
    else if (action === 'interact') {
      // A panel already open (the interact panel's own Close, a locked door's
      // Use-key/Close, ...) means Interact should activate its focused button --
      // the dialogue-box behavior this keybinding is meant to cover -- rather
      // than trying to re-open the interact panel from underneath it.
      if (!openPanelId || !activateFocusedPanelButton()) {
        document.getElementById('interact-action-button')?.click();
      }
    } else if (action === 'mark') document.getElementById('mark-action-button')?.click();
    // Routed through the buttons, like Interact and Mark above, rather than
    // calling triggerWait/triggerUndo -- so the disabled state does the work.
    // A click on a disabled button is a no-op, which is exactly what an Undo
    // with nothing to undo should be, and what keeps the key from needing its
    // own copy of "is there a turn to go back to".
    else if (action === 'wait') document.getElementById('wait-action-button')?.click();
    else if (action === 'undo') document.getElementById('undo-action-button')?.click();
    else if (action === 'runes') {
      activeMainTab = 'runes';
      openPanel('main-panel');
      renderMainPanelContent();
    } else if (action === 'inventory') {
      activeMainTab = 'inventory';
      openPanel('main-panel');
      renderMainPanelContent();
    } else {
      const index = Number(action.slice(-1)) - 1;
      document.querySelectorAll<HTMLButtonElement>('#quadrant-grid .quadrant-slot')[index]?.click();
    }
  });
  window.addEventListener('keyup', (e) => {
    if (findActionForCode(e.code) === 'cast') stopMeasureHold();
  });
  onKeybindingsChange(() => {
    renderQuadrantGrid();
    renderActionButtons();
  });

  // Tap the result box to dismiss it once read, rather than waiting for the
  // next cast to overwrite it.
  document.getElementById('cast-result-hud')?.addEventListener('click', (e) => {
    (e.currentTarget as HTMLElement).innerHTML = '';
  });

  document.querySelectorAll<HTMLButtonElement>('.ui-close').forEach((btn) => {
    btn.addEventListener('click', () => {
      const target = btn.dataset.close;
      if (target) closePanel(target);
    });
  });

  const radios = document.querySelectorAll<HTMLInputElement>('input[name="display-mode"]');
  radios.forEach((radio) => {
    radio.checked = radio.value === getDisplayMode();
    radio.addEventListener('change', () => {
      if (radio.checked) setDisplayMode(radio.value as RuneDisplayMode);
    });
  });

  const castTextRadios = document.querySelectorAll<HTMLInputElement>('input[name="cast-text-mode"]');
  castTextRadios.forEach((radio) => {
    radio.checked = radio.value === getCastTextMode();
    radio.addEventListener('change', () => {
      if (radio.checked) setCastTextMode(radio.value as CastTextMode);
    });
  });

  const spriteRadios = document.querySelectorAll<HTMLInputElement>('input[name="sprite-id"]');
  spriteRadios.forEach((radio) => {
    radio.checked = radio.value === getSpriteId();
    radio.addEventListener('change', () => {
      if (radio.checked) setSpriteId(radio.value as SpriteId);
    });
  });

  const movementRadios = document.querySelectorAll<HTMLInputElement>('input[name="movement-input-mode"]');
  movementRadios.forEach((radio) => {
    radio.checked = radio.value === getMovementInputMode();
    radio.addEventListener('change', () => {
      if (radio.checked) {
        setMovementInputMode(radio.value as MovementInputMode);
        applyMovementInputMode();
      }
    });
  });
  applyMovementInputMode();

  const clockRadios = document.querySelectorAll<HTMLInputElement>('input[name="clock-mode"]');
  clockRadios.forEach((radio) => {
    radio.checked = radio.value === getClockMode();
    radio.addEventListener('change', () => {
      if (radio.checked) setClockMode(radio.value as ClockMode);
    });
  });
  refreshTurnHud();
  onUndoAvailabilityChange(setUndoAvailable);
  setUndoAvailable(canUndo());

  // Click, not pointerdown, unlike the D-pad below: these are discrete one-shot
  // actions rather than something you hold, and a Wait that fired on pointerdown
  // and again on a stray second touch would silently burn a turn.
  document.getElementById('wait-action-button')?.addEventListener('click', () => {
    if (isMenuOpen()) return;
    triggerWait();
  });
  document.getElementById('undo-action-button')?.addEventListener('click', () => {
    if (isMenuOpen()) return;
    triggerUndo();
  });

  // D-pad buttons are plain DOM, outside the Phaser canvas -- pointerdown/up/leave
  // (not click) so holding a direction keeps walking the same way holding an
  // arrow key does, and releasing off the button edge still stops movement.
  document.querySelectorAll<HTMLButtonElement>('.dpad-btn').forEach((btn) => {
    const dir = btn.dataset.dir as DpadDirection;
    btn.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      triggerDpadDirection(dir);
    });
    btn.addEventListener('pointerup', () => triggerDpadRelease());
    btn.addEventListener('pointerleave', () => triggerDpadRelease());
    btn.addEventListener('pointercancel', () => triggerDpadRelease());
  });

  const devModeToggle = document.getElementById('dev-mode-toggle') as HTMLInputElement | null;
  if (devModeToggle) {
    devModeToggle.checked = getDevMode();
    devModeToggle.addEventListener('change', () => setDevMode(devModeToggle.checked));
  }

  // The only way back to hidden once the Menu-button secret has unlocked this
  // panel -- otherwise there'd be no way to re-hide it short of clearing
  // localStorage. Resets every other dev-only behavior toggle too, same as
  // unchecking the old title-screen checkbox did.
  // Downloads the current room's take, and says what the self-check found (see
  // moveRecorder.ts). Reporting the check rather than only the file is the point:
  // a take is a reconstruction of what the game did, so "it replays to the same
  // world" is the thing that makes it worth sending to anybody.
  document.getElementById('download-take-button')?.addEventListener('click', () => {
    const result = document.getElementById('download-take-result');
    const check = downloadTake();
    if (!result) return;
    if (!check) {
      result.textContent = 'No take yet -- walk into a room first.';
      return;
    }
    const moves = takeCommandCount();
    const where = takeRoomId() ?? 'this room';
    result.textContent = check.matches
      ? `Saved ${moves} move${moves === 1 ? '' : 's'} from ${where}. Replays to the same world.`
      : `Saved ${moves} move${moves === 1 ? '' : 's'} from ${where}, but replaying it diverges: ${check.differences.slice(0, 3).join('; ')}`;
  });

  document.getElementById('hide-dev-tools-button')?.addEventListener('click', () => {
    setDevToolsEnabled(false);
    resetDeveloperSettingsToPlayerDefaults();
    closePanel('dev-tools-panel');
  });

  const showDescriptionsToggle = document.getElementById('show-rune-descriptions-toggle') as HTMLInputElement | null;
  if (showDescriptionsToggle) {
    showDescriptionsToggle.checked = getShowRuneDescriptions();
    showDescriptionsToggle.addEventListener('change', () => setShowRuneDescriptions(showDescriptionsToggle.checked));
  }

  const showEnergyDebugToggle = document.getElementById('show-energy-debug-toggle') as HTMLInputElement | null;
  if (showEnergyDebugToggle) {
    showEnergyDebugToggle.checked = getShowEnergyDebug();
    showEnergyDebugToggle.addEventListener('change', () => setShowEnergyDebug(showEnergyDebugToggle.checked));
  }

  const showChargeDebugToggle = document.getElementById('show-charge-debug-toggle') as HTMLInputElement | null;
  if (showChargeDebugToggle) {
    showChargeDebugToggle.checked = getShowChargeDebug();
    showChargeDebugToggle.addEventListener('change', () => setShowChargeDebug(showChargeDebugToggle.checked));
  }

  const showConductivityDebugToggle = document.getElementById('show-conductivity-debug-toggle') as HTMLInputElement | null;
  if (showConductivityDebugToggle) {
    showConductivityDebugToggle.checked = getShowConductivityDebug();
    showConductivityDebugToggle.addEventListener('change', () => setShowConductivityDebug(showConductivityDebugToggle.checked));
  }

  const showHealthDebugToggle = document.getElementById('show-health-debug-toggle') as HTMLInputElement | null;
  if (showHealthDebugToggle) {
    showHealthDebugToggle.checked = getShowHealthDebug();
    showHealthDebugToggle.addEventListener('change', () => setShowHealthDebug(showHealthDebugToggle.checked));
  }

  const showEntropyDebugToggle = document.getElementById('show-entropy-debug-toggle') as HTMLInputElement | null;
  if (showEntropyDebugToggle) {
    showEntropyDebugToggle.checked = getShowEntropyDebug();
    showEntropyDebugToggle.addEventListener('change', () => setShowEntropyDebug(showEntropyDebugToggle.checked));
  }

  const showFacingDebugToggle = document.getElementById('show-facing-debug-toggle') as HTMLInputElement | null;
  if (showFacingDebugToggle) {
    showFacingDebugToggle.checked = getShowFacingDebug();
    showFacingDebugToggle.addEventListener('change', () => {
      setShowFacingDebug(showFacingDebugToggle.checked);
      renderActionButtons();
    });
  }

  const showGridCoordsToggle = document.getElementById('show-grid-coords-toggle') as HTMLInputElement | null;
  if (showGridCoordsToggle) {
    showGridCoordsToggle.checked = getShowGridCoords();
    showGridCoordsToggle.addEventListener('change', () => setShowGridCoords(showGridCoordsToggle.checked));
  }

  const showDevSignpostsToggle = document.getElementById('show-dev-signposts-toggle') as HTMLInputElement | null;
  if (showDevSignpostsToggle) {
    showDevSignpostsToggle.checked = getShowDevSignposts();
    showDevSignpostsToggle.addEventListener('change', () => setShowDevSignposts(showDevSignpostsToggle.checked));
  }

  document.getElementById('intro-continue-button')?.addEventListener('click', () => closePanel('intro-panel'));
  document.getElementById('death-restart-button')?.addEventListener('click', () => {
    closePanel('death-panel');
    // GameScene's onRoomRestart listener rebuilds the current room and
    // restores Health/Energy/Charge -- see roomRestart.ts.
    triggerRoomRestart();
  });
  document.getElementById('equip-tutorial-continue-button')?.addEventListener('click', () => closePanel('equip-tutorial-panel'));
  document.getElementById('cast-tutorial-continue-button')?.addEventListener('click', () => closePanel('cast-tutorial-panel'));
  document.getElementById('modifier-tutorial-continue-button')?.addEventListener('click', () => closePanel('modifier-tutorial-panel'));
  document.getElementById('transfer-tutorial-continue-button')?.addEventListener('click', () => closePanel('transfer-tutorial-panel'));
  document.getElementById('notebook-tutorial-continue-button')?.addEventListener('click', () => closePanel('notebook-tutorial-panel'));

  const titleTutorialCheckbox = document.getElementById('title-tutorial-checkbox') as HTMLInputElement | null;
  titleTutorialCheckbox?.addEventListener('change', () => setTutorialEnabled(titleTutorialCheckbox.checked));
  document.getElementById('title-new-game-button')?.addEventListener('click', () => {
    // performNewGameReset() fires triggerNewGame(), which GameScene's onNewGame
    // listener handles synchronously and ends with its own showIntro() call --
    // openPanel() there hides title-panel for free, no separate close needed.
    performNewGameReset();
  });

  updateLinkIndicator();
  updateStatsHud();
  // Save/Resume and New Game controls render lazily now, inside the
  // Settings tab (renderSettingsTab), rather than needing a boot-time call
  // into containers that don't exist in the DOM until that tab is opened.
  renderQuadrantGrid();
  renderActionButtons();
}
